<template>
  <d-container
    fluid
    :class="{
      'main-content-container px-4': true,
      'has-advance-issue': hasAdvanceIssue
    }">

    <div
      v-if="hasAdvanceIssue"
      class="has-advance-issue-alert">
      {{ $t('order.noAdvanceInfo') }}
    </div>

    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="4" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.offersAndOrders') }}</span>
        <h3 class="page-title">
          <template v-if="isEditMode">{{ $t('ui.edit') }}
            <template v-if="isOrder || isOrderMode">{{ $t('order.orderAccusative') }}</template>
            <template v-else>{{ $t('order.offerAccusative') }}</template>
            <template v-if="isIndentList"> {{ $t('indentRegistry.indent') }}</template>
          </template>
          <template v-else>
            {{ $t('ui.add') }}
            <template v-if="isOrder || isOrderMode">{{ $t('order.orderAccusative') }}</template>
            <template v-else>{{ $t('order.offerAccusative') }}</template>
            <template v-if="isIndentList"> {{ $t('indentRegistry.indent') }}</template>
          </template>

          <d-button
            v-if="isEditMode && userIsAdmin && orderData.can_update"
            size="sm"
            class="ml-2 px-2 py-1"
            theme="dark"
            :outline="true"
            @click.prevent="lockOrderEdit">
            <div
              v-if="lockInProgress"
              class="spinner-border spinner-border-sm" role="status"></div>
            🔒 {{ $t('order.lockOrder') }}
          </d-button>
        </h3>
      </d-col>
      <d-col col sm="8" class="text-right text-sm-right d-flex justify-content-end align-items-end flex-wrap">
        <d-button
          theme="secondary"
          class="mr-2"
          :disabled="isLoading || taxNumberCheckInProgress"
          @click.prevent="$bus.$emit('order-form-back-button-clicked'); $router.push(backUrl)">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-dropdown
          v-if="canSendInvoice && !invoiceRequestInProgress"
          :text="$t('order.requestInvoice')"
          theme="warning"
          class="mr-2"
          :disabled="settlementInvoiceInProgress !== false || isLoading || taxNumberCheckInProgress || orderSetUpInProgress || customerChangedWithoutSave"
          :title="customerChangedWithoutSave ? $t('order.saveOrderWithNewCustomerToRequestInvoice') : null"
          key="request-invoice-button">
          <d-dropdown-item>
            <a
              @click.prevent="checkCustomerInvoiceDataBeforeInvoice(false)">
              <template v-if="!settlementInvoiceInProgress && !taxNumberCheckInProgress">{{ $t('order.requestInvoice') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item v-if="userIsAdmin">
            <a
              @click.prevent="checkCustomerInvoiceDataBeforeInvoice(true)">
              <template v-if="!settlementInvoiceInProgress && !taxNumberCheckInProgress">{{ $t('order.requestInvoice') }} ({{ $t('ui.immediate') }})</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
        </d-dropdown>

        <d-button
          v-if="invoiceRequestInProgress"
          :disabled="true"
          theme="warning"
          :outline="true"
          class="mr-2"
          key="request-sent-button"
          v-pure-html="$t('order.invoiceRequestSent')">
        </d-button>

        <d-button
          v-if="!invoiceCancelRequestSent && (invoiceRequestInProgress || canResetInvoiceRequest)"
          :disabled="invoiceResetInProgress"
          theme="danger"
          :outline="true"
          class="mr-2"
          @click.prevent="resetInvoiceRequest"
          v-pure-html="invoiceResetInProgress ? spinnerTemplate : $t('order.invoiceRequestCancel')">
        </d-button>

        <d-dropdown
          v-if="isEditMode"
          :text="$t('order.download')"
          theme="info"
          class="mr-2">
          <d-dropdown-item>
            <a
              :disabled="downloadingPdf !== false || orderSetUpInProgress"
              @click.prevent="downloadPdf()"
              :style="'display: block;' + ((downloadingPdf !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingPdf">{{ $t('ui.pdf') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item>
            <a
              :disabled="downloadingXls !== false || orderSetUpInProgress"
              @click.prevent="downloadXls()"
              :style="'display: block;' + ((downloadingXls !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingXls">{{ $t('ui.xls') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item>
            <a
              :disabled="downloadingSummary !== false || orderSetUpInProgress"
              @click.prevent="downloadSummary()"
              :style="'display: block;' + ((downloadingSummary !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingSummary">{{ $t('order.summary') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item v-if="settlements && !settlements.length">
            <a
              :disabled="downloadingProforma !== false || orderSetUpInProgress"
              @click.prevent="downloadProforma()"
              :style="'display: block;' + ((downloadingProforma !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingProforma">{{ $t('order.downloadProforma') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item>
            <a
              :disabled="downloadingStock !== false || orderSetUpInProgress"
              @click.prevent="downloadStock()"
              :style="'display: block;' + ((downloadingStock !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingStock">{{ $t('order.downloadStock') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
        </d-dropdown>

        <d-button
          class="mt-1"
          theme="success"
          :disabled="isLoading || taxNumberCheckInProgress || confirmationsCheckInProgress || orderSetUpInProgress"
          @click.prevent="$bus.$emit('order-form-save-button-clicked'); validateForm()">
          <template v-if="isLoading || taxNumberCheckInProgress || confirmationsCheckInProgress">
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </template>
          <template v-else>
            <i class="material-icons">
              <template v-if="isEditMode">edit</template>
              <template v-else>add</template>
            </i>

            <template v-if="isEditMode && !(isOrderMode || isOrder)">{{ $t('order.saveOffer') }}</template>
            <template v-if="isEditMode && (isOrderMode || isOrder)">{{ $t('purchase.saveOrder') }}</template>
            <template v-if="!isEditMode && !(isOrderMode || isOrder)">{{ $t('order.addOffer') }}</template>
            <template v-if="!isEditMode && (isOrderMode || isOrder)">{{ $t('purchase.addOrder') }}</template>
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="!userIsCustomer && orderData && orderData.invoice_country_prefix !== 'PL' && orderData.customer && !orderData.customer.last_correct_vies_verification_at && invoiceCountryPrefixIsVerified"
      show
      theme="danger">
      {{ $t('order.orderWontBeSentToErpDueLackOfVerifiedVIES') }}
    </d-alert>

    <d-alert
      v-if="!userIsCustomer && orderData && orderData.incorrect_import"
      show
      theme="danger">
      {{ $t('softlab.orderImportToSoftlabError') }}<template v-if="orderData && orderData.incorrect_import_at"> ({{ orderData.incorrect_import_at }})</template>.
      <template v-if="orderData && orderData.incorrect_import_description">
        <br><strong class="font-weight-bold">{{ $t('softlab.errorDescription') }}:</strong> {{ orderData.incorrect_import_description }}
      </template>
    </d-alert>

    <d-alert
      v-if="is_priority"
      show
      theme="warning">
      {{ $t('order.attentionPriorityOrder') }}
    </d-alert>

    <d-alert
      v-if="!confirm_order"
      show
      theme="info">
      {{ $t('order.confirmOrderDisabled') }}
    </d-alert>

    <d-alert
      v-if="orderData && orderData.collect_alert"
      :show="showCollectAlert"
      theme="warning"
      class="d-flex">
      <div>{{ $t('order.orderCollectedPartiallyOutOfStock') }}</div>
      <i
        class="material-icons ml-auto"
        style="cursor: pointer"
        @click="dismissCollectAlert">close</i>
    </d-alert>

    <d-alert
      :show="showEmbroideryAlert"
      theme="danger"
      class="d-flex">
      {{ $t('order.warehouse.boxes.info.embroideryAlert') }}
    </d-alert>

    <d-alert
      :show="showBoxesAlert"
      theme="danger"
      class="d-flex">
      {{ $t('order.warehouse.boxes.info.boxesAlert') }}
    </d-alert>

    <d-alert
      v-if="orderData && orderData.collect_warning"
      theme="warning"
      :show="true"
      class="d-flex">
      <div>{{ $t('order.collectedProductsCountDifferentThanOrdered') }}</div>
    </d-alert>

    <d-alert
      v-if="preferredCartonsAmount > 0 && preferredCartonsAmount !== shipper_cartons_number"
      theme="warning"
      :show="true">
      {{ $t('order.checkCartonsAmount') }}
    </d-alert>

    <!-- Form -->
    <d-form :class="{
      'is-loading': isLoading || taxNumberCheckInProgress || confirmationsCheckInProgress
    }">
      <validation-observer ref="order-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            v-sticky-tabs
            ref="order-tabs"
            card
            :nav-class="allDataLoaded ? validationResults : ''">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <d-alert
                v-if="is_special_requirement"
                :show="true"
                theme="danger"
                class="pt-4 pb-4 has-special-requirements">
                <i class="material-icons">warning</i>
                {{ $t('order.hasSpecialRequirementsWarning') }}
              </d-alert>

              <d-alert
                v-if="trackingNumberIsLocked || (tracking_number && (!invoiceRequestInProgress && noInvoiceRequests && status !== 'SENT'))"
                :show="true"
                theme="warning"
                class="pt-4 pb-4 has-special-requirements">
                <i class="material-icons">warning</i>
                {{ $t('order.createInvoiceOrChangeToSent') }}
              </d-alert>

              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}

                <div class="ml-auto order-priority-checkbox">
                  <d-checkbox
                    inline
                    v-model="is_priority"
                    toggle
                    toggle-small>
                    {{ $t('order.priorityCap') }}
                  </d-checkbox>
                </div>

                <div class="ml-2 order-confirmation-checkbox">
                  <d-checkbox
                    inline
                    v-model="confirm_order"
                    toggle
                    toggle-small>
                    {{ $t('order.confirmOrder') }}
                  </d-checkbox>
                </div>

                <div class="ml-2 order-status-wrapper">
                  <order-status
                    v-if="isEditMode && !orderSetUpInProgress"
                    :isLocked="isLocked && !userIsAdmin"
                    :initialStatus="status"
                    :settlementsNumber="orderData.settlements.length" />
                </div>
              </h4>

              <d-row>
                <d-col
                  cols="12"
                  class="d-flex align-middle">
                  <span
                    v-if="exclude_from_erp_integration"
                    class="text-danger">
                    {{ $t('order.erp.isExcluded') }}
                  </span>

                  <d-badge
                    v-if="isEditMode && !orderData.is_virtual && !exclude_from_erp_integration"
                    :theme="orderData.erp_optima_updated_at ? 'success' : 'danger'"
                    class="mr-3"
                    :outline="true"
                    size="lg"
                    @click.native="openErpIntegrationPopup"
                    style="cursor: pointer; font-size: 18px;">
                    <template v-if="orderData.erp_optima_updated_at">
                      {{ $t('order.erp.integratedWithErp') }}
                    </template>
                    <template v-else>
                      {{ $t('order.erp.notIntegratedWithErp') }}
                    </template>
                  </d-badge>

                  <d-button
                    v-if="!exclude_from_erp_integration && userIsAdmin"
                    theme="danger"
                    @click.prevent="excludeFromERP">
                    <div
                      v-if="excludeFromErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.excludeFrom') }}
                  </d-button>

                  <d-button
                    v-if="exclude_from_erp_integration && userIsAdmin"
                    theme="success"
                    class="ml-3"
                    @click.prevent="includeToERP">
                    <div
                      v-if="includeToErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.includeTo') }}
                  </d-button>

                  <d-checkbox
                    inline
                    v-model="block_erp_invoice_request"
                    :disabled="!is_advance"
                    toggle
                    toggle-small
                    class="ml-3 mb-0">
                    {{ $t('order.blockErpInvoiceRequest') }}
                  </d-checkbox>
                </d-col>
              </d-row>

              <hr>

              <d-row
                v-if="(isIndentList && !isOffer) || showBlockadeButton || showCustomerApprovalButton || showSendToHafciarniaButton || showSendAgainToHafciarniaButton"
                key="additional-order-buttons">
                <d-col
                  v-if="isIndentList && !isOffer"
                  cols="5"
                  class="text-left d-flex">
                  <label class="pt-1 pr-3">
                    {{ $t('container.container') }}:
                  </label>
                  <div
                    v-if="!containerName"
                    :class="{
                      'form-control': true,
                      'is-select': true
                    }">
                    <v-select
                      class="w-100"
                      :options="containerOptions"
                      v-model="container_id"
                      :placeholder="$t('container.selectContainer')"
                      :searchable="true"
                      label="text"
                      :reduce="container => container.value">
                      <template slot="no-options">
                        {{ $t('container.noContainersMatchingPhraseFound') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }} ({{ option.date }})
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }} ({{ option.date }})
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <span
                    v-if="containerName"
                    class="text-primary pt-1">
                    {{ containerName }}
                    <small>
                      <a
                        href="#"
                        @click.prevent="containerName = ''; container_id = null;">
                        <i class="material-icons">edit</i>
                        {{ $t('ui.edit') }}
                      </a>
                    </small>
                  </span>
                </d-col>
                <d-col
                  :cols="isIndentList && !isOffer ? 7 : 12"
                  class="text-right">
                  <d-button
                    v-if="showBlockadeButton"
                    theme="danger"
                    class="ml-2"
                    outline
                    size="sm"
                    key="additiona-order-buttons-blockade-button"
                    @click.native="configureBlockade = true;">
                    {{ $t('order.lock.orderLock') }}
                  </d-button>

                  <d-button
                    v-if="showCustomerApprovalButton"
                    theme="primary"
                    class="ml-2"
                    key="additiona-order-buttons-send-for-customer-approval-button"
                    @click.prevent="notifyClient">
                    <i class="material-icons">cloud_upload</i>
                    {{ $t('inquiries.sendForCustomerApproval') }}
                  </d-button>

                  <d-button
                    v-if="showFollowUpButton"
                    theme="secondary"
                    class="ml-2"
                    key="additiona-order-buttons-send-followup-button"
                    @click.prevent="sendFollowUp"
                    :disabled="followupInProgress || followUpSent">
                    <div
                      v-if="followupInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    <i
                      v-else
                      class="material-icons">cloud_upload</i>
                    {{ $t('inquiries.sendFollowUp') }}
                  </d-button>

                  <d-button
                    v-if="showSendToHafciarniaButton && !showSendAgainToHafciarniaButton"
                    theme="primary"
                    :outline="true"
                    class="ml-2"
                    key="additiona-order-buttons-send-to-hafciarnia-button"
                    @click.prevent="sendToHafciarnia">
                    <i class="material-icons">cloud_upload</i>
                    {{ $t('order.hafciarnia.sendToHafciarnia') }}
                  </d-button>

                  <d-button
                    v-if="showSendAgainToHafciarniaButton && !showSendToHafciarniaButton"
                    theme="primary"
                    :disabled="sendingAgainToEmbroideryInProgress || hasBeenSendAgainToEmbroidery"
                    class="ml-2"
                    @click.prevent="sendAgainToEmbroidery">
                    <div
                      v-if="sendingAgainToEmbroideryInProgress"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    {{ $t('order.hafciarnia.sendAgainToEmbroidery') }}
                  </d-button>
                </d-col>
              </d-row>

              <hr v-if="(isIndentList && !isOffer) || showBlockadeButton || showCustomerApprovalButton || showSendToHafciarniaButton">

              <d-row
                v-if="showBlockadeBlock"
                key="blockade-status-select">
                <d-col
                  cols="6">
                  <label>{{ $t('order.lock.configureOrderLock') }}:</label>
                  <d-form-select
                    v-model="blockade_status"
                    :options="[{
                      value: null,
                      text: $t('order.lock.noLock')
                    }, {
                      value: 'UNBLOCK',
                      text: $t('order.lock.unBlock')
                    }, {
                      value: 'BLOCKADE',
                      text: $t('order.lock.orderLock')
                    }, {
                      value: 'RETURN_TO_STOCK',
                      text: $t('order.returnToStock')
                    }]">
                  </d-form-select>

                  <small
                    v-if="blockade_status === 'RETURN_TO_STOCK'"
                    class="text-danger">
                    {{ $t('order.lock.returnPrductWarningInfo') }}
                  </small>
                </d-col>

                <d-col
                  v-if="blocked_at || blockade_returned_at || blockade_returned_by"
                  cols="6"
                  class="d-flex align-items-center">
                  <div>
                    <template v-if="blocked_at">
                      <small>
                        <strong>{{ $t('order.lock.dateOfBlocking') }}:</strong>
                        {{ blocked_at }}
                      </small><br>
                    </template>
                    <template v-if="blockade_returned_by">
                      <small>
                        <strong>{{ $t('order.lock.warehousemanReturningProducts') }}:</strong>
                        {{ blockade_returned_by }}
                      </small><br>
                    </template>
                    <template v-if="blockade_returned_at">
                      <small>
                        <strong>{{ $t('order.lock.dateOfProductsReturn') }}</strong>
                        {{ blockade_returned_at }}
                      </small>
                    </template>
                  </div>
                </d-col>
              </d-row>

              <hr v-if="showBlockadeBlock">

              <d-row
                v-if="realization_date !== null"
                key="realization-date-from-customer">
                <d-col cols="12">
                  <label>{{ $t('order.preferedByClientExecutionDate') }}:</label> <span class="text-primary">{{ realizationDate }}</span>
                </d-col>
              </d-row>

              <hr v-if="realization_date !== null">

              <d-row class="mb-2">
                <d-col cols="4">
                  <label>
                    {{ $t('ui.orderName') }}
                  </label>

                  <validation-provider
                    :rules="isEditMode ? 'required|value_contains:' + orderData.id : 'required'"
                    name="order_name"
                    v-slot="{ errors }">
                    <d-input
                      :state="errors.length ? 'invalid' : null"
                      v-model="order_name"
                      @input="debouncedOrderNameCheck()"
                      type="text" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                    <small
                      v-if="orderNameCheckInProgress"
                      class="text-gray mt-2">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                      {{ $t('order.nameCheck.inProgress') }}
                    </small>
                    <small
                      v-if="order_name && !orderNameCheckInProgress && orderNameInUse > 0"
                      class="text-danger mt-2">
                      <i class="material-icons">block</i>
                      {{ $t('order.nameCheck.inUse') }}
                      <span
                        @click="showOrderNamePopup()"
                        style="text-decoration: underline; cursor: pointer;">
                        {{ $t('order.nameCheck.inUseCTA', { count: orderNameInUse }) }}
                      </span>
                    </small>
                    <small
                      v-if="order_name && !orderNameCheckInProgress && orderNameInUse === 0"
                      class="text-success mt-2">
                      <i class="material-icons">check</i>
                      {{ $t('order.nameCheck.isAvailable') }}
                    </small>
                  </validation-provider>
                </d-col>

                <d-col cols="4">
                  <label>
                    {{ $t('ui.orderNameNotes') }}
                  </label>

                  <d-input
                    v-model="order_name_notes"
                    type="text" />
                </d-col>

                <d-col cols="4">
                  <order-select-worker
                    v-if="orderData"
                    :current-worker-id="orderData.user_id"
                    :order-id="orderData.id" />
                </d-col>
              </d-row>

              <d-row class="mb-3">
                <d-col cols="4">
                  <d-checkbox
                    inline
                    v-model="is_repeated"
                    toggle
                    toggle-small
                    class="mb-0">
                    {{ $t('order.isRepeated') }}
                  </d-checkbox>

                  <d-input
                    v-if="is_repeated"
                    v-model="repeat_number"
                    :placeholder="$t('order.repeatNumber')"
                    type="text" />
                </d-col>

                <d-col cols="4">
                  <d-checkbox
                    inline
                    v-model="is_box_marking"
                    toggle
                    toggle-small
                    class="mb-0">
                    {{ $t('order.isBoxMarking') }}
                  </d-checkbox>

                  <d-input
                    v-if="is_box_marking"
                    v-model="box_marking_number"
                    :placeholder="$t('order.boxMarkingNumber')"
                    type="text" />
                </d-col>

                <d-col cols="4">
                  <d-checkbox
                    inline
                    v-model="is_special_requirement"
                    toggle
                    toggle-small
                    class="mb-0">
                    {{ $t('order.isSpecialRequirement') }}
                  </d-checkbox>

                  <d-textarea
                    v-if="is_special_requirement"
                    v-model="special_requirement"
                    :value.prop="special_requirement"
                    :class="{
                      'special-requirements-not-empty': !!special_requirement
                    }"
                    :placeholder="$t('order.specialRequirement')"
                    :rows="3" />
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <label>
                    {{ $t('order.transactionType') }}
                  </label>

                  <validation-provider
                    rules="required"
                    name="transaction_type"
                    v-slot="{ errors }">
                    <d-form-select
                      v-model="transaction_type"
                      :state="errors.length ? 'invalid' : null"
                      :disabled="!custom_transaction_type && (blockExportTransactionType || blockDomesticTransactionType || blockNoEuVatTransactionType)"
                      :options="[{
                        value: null,
                        text: $t('order.selectTransactionType')
                      }, {
                        value: 'DOMESTIC',
                        text: $t('order.transactionTypes.domestic')
                      }, {
                        value: 'UE',
                        text: $t('order.transactionTypes.eu')
                      }, {
                        value: 'EXPORT',
                        text: $t('order.transactionTypes.export')
                      }, {
                        value: 'NO_EU_VAT',
                        text: $t('order.transactionTypes.noVatEU')
                      }]">
                    </d-form-select>

                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>

                  <d-checkbox
                    inline
                    v-model="custom_transaction_type"
                    toggle
                    toggle-small
                    :disabled="!userIsAdmin"
                    class="mt-2">
                    {{ $t('order.customTransactionType') }}
                  </d-checkbox>
                </d-col>

                <d-col cols="3">
                  <label class="d-flex">
                    <span
                      v-if="hasDeadlineWarning()"
                      class="text-danger">
                      {{ $t('ui.deadlineCap') }} <i class="material-icons">warning</i>
                    </span>
                    <span v-else class="text-danger">
                      {{ $t('ui.deadlineCap') }}
                    </span>

                    <a
                      v-if="deadline"
                      href="#"
                      class="ml-auto text-danger"
                      @click.prevent="removeDate('deadline')">
                      <small>
                        {{ $t('order.removeDate')}}
                      </small>
                    </a>
                  </label>

                  <d-datepicker
                    v-model="deadline"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    class="text-danger"
                    :language="translationsForDatePicker" />
                </d-col>

                <d-col cols="3">
                  <label class="d-flex">
                    <small class="pt-1">
                      <strong>
                        {{ $t('order.estimatedShippmentDate') }}
                      </strong>
                    </small>
                    <a
                      v-if="shipper_date"
                      href="#"
                      class="ml-auto text-danger"
                      @click.prevent="removeDate('shipper_date')">
                      <small>
                        {{ $t('order.removeDate')}}
                      </small>
                    </a>
                  </label>

                  <d-datepicker
                    v-model="shipper_date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :language="translationsForDatePicker" />
                </d-col>

                <d-col cols="3">
                  <label class="d-flex">
                    <small class="pt-1">
                      <strong>
                        {{ $t('order.remindMeAfter') }}
                      </strong>
                    </small>

                    <a
                      v-if="stash_to"
                      href="#"
                      class="ml-auto text-danger"
                      @click.prevent="removeDate('stash_to')">
                      <small>
                        {{ $t('order.removeDate')}}
                      </small>
                    </a>
                  </label>

                  <d-datepicker
                    v-model="stash_to"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :disabled-dates="pastDates"
                    :language="translationsForDatePicker" />
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="2">
                  <d-checkbox
                    inline
                    v-model="is_virtual"
                    toggle
                    toggle-small
                    @click.native="checkIfCanBeVirtualOrder">
                    {{ $t('order.isVirtual') }}
                  </d-checkbox>
                </d-col>

                <d-col cols="2">
                  <d-checkbox
                    inline
                    v-model="is_advance"
                    toggle
                    toggle-small>
                    {{ $t('order.requiresAdvance') }}
                  </d-checkbox>
                </d-col>

                <d-col cols="2">
                  <d-checkbox
                    inline
                    v-model="is_cod"
                    toggle
                    toggle-small>
                    {{ $t('order.cod') }}
                  </d-checkbox>
                </d-col>

                <d-col
                  v-if="is_advance"
                  cols="3">
                  <d-row cols="6">
                    <d-col>
                      <label class="mt-1 mb-0">
                        {{ $t('order.advance') }}
                      </label>
                    </d-col>

                    <d-col cols="6">
                      <validation-provider
                        rules="required|min_value:0|max_value:100"
                        name="advance_percent"
                        v-slot="{ errors }">
                        <d-input-group :class="{ 'is-invalid': errors && errors.length }">
                          <d-input
                            :min="0"
                            :max="100"
                            :step="1"
                            :state="errors.length ? 'invalid' : null"
                            v-model="advance_percent "
                            type="number" />
                          <d-input-group-text slot="append">
                            %
                          </d-input-group-text>
                        </d-input-group>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </d-col>
                  </d-row>
                </d-col>

                <d-col
                  v-if="is_advance"
                  cols="3"
                  md="3"
                  lg="3">
                  <d-checkbox
                    inline
                    v-model="advance_paid"
                    toggle
                    toggle-small
                    :class="{ 'is-unpaid-alert': !advance_paid }">
                    {{ $t('order.advancePaid') }}
                  </d-checkbox>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="12" lg="8" md="8">
                  <d-alert
                    v-if="include_discounts && hasDifferentDiscounts"
                    show
                    theme="warning"
                    v-pure-html="$t('customer.discount.currentCustomerDiscountsDifferentFromThoseInOrder')">
                  </d-alert>

                  <order-select-customer
                    :includeDiscounts="include_discounts"
                    ref="order-customer"
                    :orderDataInvoice="orderDataInvoice"
                    :userIsCustomer="userIsCustomer"
                    :isIndent="isIndentList" />

                  <d-row
                    v-if="include_discounts"
                    class="mb-2">
                    <d-col cols="3">
                      <label>
                        <small>{{ $t('customer.discount.productDiscount') }}:</small>
                      </label>
                      <validation-provider
                        rules="required|min_value:0|max_value:100"
                        name="discount_products"
                        v-slot="{ errors }">
                        <d-input-group :class="{ 'is-invalid': errors && errors.length }">
                          <d-input
                            :min="0"
                            :max="100"
                            :step="0.1"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="discount_products"
                            type="number" />
                          <d-input-group-text slot="append">
                            %
                          </d-input-group-text>
                        </d-input-group>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </d-col>

                    <d-col cols="3">
                      <label>
                        <small>{{ $t('customer.discount.servicesDiscount') }}:</small>
                      </label>

                      <validation-provider
                        rules="required|min_value:0|max_value:100"
                        name="discount_services"
                        v-slot="{ errors }">
                        <d-input-group :class="{ 'is-invalid': errors && errors.length }">
                          <d-input
                            :min="0"
                            :max="100"
                            :step="0.1"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="discount_services"
                            type="number" />
                          <d-input-group-text slot="append">
                            %
                          </d-input-group-text>
                        </d-input-group>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </d-col>

                    <d-col cols="3">
                      <label>
                        <small>{{ $t('customer.discount.deliveryDiscount') }}:</small>
                      </label>

                      <validation-provider
                        rules="required|min_value:0|max_value:100"
                        name="discount_shipping"
                        v-slot="{ errors }">
                        <d-input-group :class="{ 'is-invalid': errors && errors.length }">
                          <d-input
                            :min="0"
                            :max="100"
                            :step="0.1"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="discount_shipping"
                            @input="updateShipmentCost(true)"
                            type="number" />
                          <d-input-group-text slot="append">
                            %
                          </d-input-group-text>
                        </d-input-group>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </d-col>

                    <d-col
                      cols="3"
                      class="d-flex flex-wrap align-content-end">
                      <d-button
                        v-if="hasDifferentDiscounts"
                        outline
                        theme="danger"
                        class="w-100"
                        :disabled="!allDataLoaded || !$refs['order-customer'].selectedCustomer"
                        @click.prevent="resetOrderDiscounts">
                        {{ $t('customer.discount.resetDiscoutns') }}
                      </d-button>
                    </d-col>
                  </d-row>

                  <d-row class="mb-5">
                    <d-col cols="3">
                      <label>
                        <small>Skonto:</small>
                      </label>

                      <validation-provider
                        rules="min_value:0"
                        name="skonto"
                        v-slot="{ errors }">
                        <d-input-group :class="{ 'is-invalid': errors && errors.length }">
                          <d-input
                            :min="0"
                            :step="0.01"
                            class="text-right"
                            :state="errors.length ? 'invalid' : null"
                            v-model="skonto"
                            type="number" />
                          <d-input-group-text slot="append">
                            <template v-if="$refs['order-currency']">
                              {{ $refs['order-currency'].selectedCurrencySymbol }}
                            </template>
                          </d-input-group-text>
                        </d-input-group>

                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </d-col>
                  </d-row>
                </d-col>

                <d-col cols="12" lg="4" md="4" class="align-bottom">
                  <d-checkbox
                    inline
                    v-model="include_discounts"
                    toggle
                    toggle-small>
                    {{ $t('customer.discount.includeCustomerDiscounts') }}
                  </d-checkbox><br>

                  <d-checkbox
                    inline
                    v-model="include_setup_prices"
                    toggle
                    toggle-small>
                    {{ $t('price.includeSetupPrices') }}
                  </d-checkbox><br>

                  <d-checkbox
                    inline
                    v-model="dates_not_required"
                    toggle
                    toggle-small>
                    {{ $t('order.samplesNotRequired') }}
                  </d-checkbox><br>

                  <d-checkbox
                    inline
                    v-model="free_freight"
                    toggle
                    toggle-small>
                    {{ $t('order.freeDelivery') }}
                  </d-checkbox>

                  <br>

                  <d-button
                    size="sm"
                    theme="primary"
                    :outline="true"
                    class="mt-3"
                    :disabled="updateInvoiceDataInProgress"
                    @click.prevent="updateInvoiceDataRequest()">
                    <template v-if="!updateInvoiceDataInProgress">
                      {{ $t('order.updateCustomerInfoOnInvoice') }}
                    </template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </d-button>
                </d-col>
              </d-row>

              <order-select-contact
                ref="order-contact"
                :customer-id="allDataLoaded ? this.$refs['order-customer'].customer_id : 0" />

              <div
                v-if="isEditMode && orderData.address_id && !addressHasBeenChanged"
                class="d-flex">
                <p class="mr-5">{{ $t('order.shippingAddress') }}:</p>

                <div class="text-muted text-bold">
                  {{ orderData.address_name }}<br>
                  <template v-if="orderData.address_phone">tel: {{ orderData.address_phone }}<br></template>
                  <template v-if="orderData.address_email">e-mail: {{ orderData.address_email }}<br></template>
                  {{ orderData.address_company_name }}<br>
                  {{ orderData.address_postcode }} {{ orderData.address_city }} {{ orderData.address_country }}<br>
                  {{ orderData.address_street }} <template v-if="orderData.address_building">{{ orderData.address_building }}<template v-if="orderData.address_locale"> / {{ orderData.address_locale }}</template></template>
                  <template v-if="orderData.address_line_2"><br>{{ orderData.address_line_2 }}</template>
                  <template v-if="orderData.address_line_3"><br>{{ orderData.address_line_3 }}</template>

                  <br>
                  <small
                    class="text-primary cursor-pointer mt-3 d-inline-block"
                    @click.prevent="addressHasBeenChanged = true; updateShipmentPrice();">
                    <i class="material-icons">edit</i>
                    {{ $t('ui.edit') }}
                  </small>
                </div>
              </div>

              <order-select-address
                :hidden="!(!isEditMode || !orderData.address_id || addressHasBeenChanged)"
                :isHiddenInUi="!(!isEditMode || !orderData.address_id || addressHasBeenChanged)"
                ref="order-address"
                :customer-id="allDataLoaded ? this.$refs['order-customer'].customer_id : 0"
                :partner-customer-id="allDataLoaded && orderData ? orderData.partner_customer_id : 0" />

              <hr>

              <d-row>
                <order-select-brand ref="order-brand" />
                <order-select-currency ref="order-currency" />
                <order-select-lang ref="order-lang" />
              </d-row>

              <hr>

              <d-row>
                <d-col
                  v-if="isEditMode && !processingTimeChangeToSave"
                  cols="12" md="3" lg="3">
                  <label for="processing-time">
                    {{ $t('order.executionTime') }}:
                  </label>
                  <br>
                  <strong>
                    {{ processing_time_name }}
                  </strong>
                  <small
                    class="text-primary cursor-pointer mt-3 d-inline-block"
                    @click.prevent="processingTimeChangeToSave = true;">
                    <i class="material-icons">edit</i>
                    {{ $t('ui.edit') }}
                  </small>
                </d-col>

                <order-select-time
                  v-show="!isEditMode || processingTimeChangeToSave"
                  :isOfferView="!isOrder"
                  ref="order-time" />
                <order-select-shipper
                  ref="order-shipper" />

                <d-col
                  cols="12" md="3" lg="3">
                  <label class="d-flex mb-1">
                    <span>{{ $t('order.shippingCost') }}:</span>
                    <d-checkbox
                      inline
                      v-model="custom_shipping_price"
                      class="ml-auto mb-0 mr-0">
                      <small>
                        {{ $t('order.shippingCostCustom') }}
                      </small>
                    </d-checkbox>
                  </label>

                  <validation-provider
                    rules="min_value:0"
                    name="shipper-price-before-discount"
                    v-slot="{ errors }">
                    <d-input-group>
                      <input
                        :class="{
                          'form-control': true,
                          'is-invalid': errors.length
                        }"
                        :invalid="errors.length ? true : null"
                        :min="0"
                        type="number"
                        :disabled="!$refs['order-shipper'].shipper_id || (!$refs['order-address'].selectedAddress && !(isEditMode && !addressHasBeenChanged && orderData.address_country_id)) || free_freight || shippingPriceUpdateInProgress"
                        v-model="shipper_price_before_discount"
                        @change="updateDiscountedShippingPrice"
                        @input="updateDiscountedShippingPrice" />
                      <d-input-group-text slot="append">
                        <div
                          v-if="shippingPriceUpdateInProgress"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        <template v-else>
                          {{ $refs['order-currency'].selectedCurrencySymbol }}
                        </template>
                      </d-input-group-text>
                    </d-input-group>
                  </validation-provider>

                  <small
                    v-if="errorDuringLoadingShippingPrice"
                    class="text-danger">
                    {{ $t('order.errorDuringLoadingShippingPrice') }}
                  </small>

                  <small
                    v-if="(
                      (
                        $refs['order-shipper'] &&
                        !$refs['order-shipper'].shipper_id
                      ) || (
                        $refs['order-address'] &&
                        !$refs['order-address'].selectedAddress &&
                        !(
                          isEditMode &&
                          !addressHasBeenChanged &&
                          orderData.address_country_id
                        )
                      )
                    ) && !free_freight"
                    class="text-danger">
                    {{ $t('order.shipperAndAddressAreNecessaryToCalculatePrice') }}
                  </small>

                  <small v-if="shipper_price && roundProperly(shipper_price) !== roundProperly(shipper_price_before_discount)">
                    <label>
                      {{ $t('order.shippingCostAfterDiscount') }}:
                    </label>

                    <span class="text-primary">
                      {{ roundProperly(shipper_price) }} {{ $refs['order-currency'].selectedCurrencySymbol }}
                    </span>
                  </small>

                  <small v-if="isEditMode && previousShippingPrice && (parseFloat(previousShippingPrice) !== parseFloat(shipper_price) || previousCurrencySymbol !== $refs['order-currency'].selectedCurrencySymbol)">
                    <label>
                      {{ $t('order.shippingCostPreviousVersion') }}:
                    </label>

                    <span class="text-danger">
                      {{ roundProperly(previousShippingPrice) }} {{ previousCurrencySymbol }}
                    </span>
                  </small>
                </d-col>

                <d-col
                  cols="12" md="3" lg="3">
                  <label>
                    {{ $t('order.cartonsNumber') }}:
                  </label>

                  <d-input-group>
                    <d-input-group-addon prepend>
                      <d-button
                        @click.prevent="decreaseBoxes"
                        @mousedown.native.prevent="continousDecreaseBoxesStart"
                        @mouseup.native.prevent="continousDecreaseBoxesStop"
                        @mouseleave.native.prevent="continousDecreaseBoxesStop"
                        :disabled="!shipper_price_before_discount">
                        <i class="material-icons">remove</i>
                      </d-button>
                    </d-input-group-addon>
                    <d-input
                      type="number"
                      min="0"
                      step="1"
                      readonly
                      class="text-center"
                      v-model="shipper_cartons_number" />
                    <d-input-group-addon append>
                      <d-button
                        @click.prevent="increaseBoxes"
                        @mousedown.native.prevent="continousIncreaseBoxesStart"
                        @mouseup.native.prevent="continousIncreaseBoxesStop"
                        @mouseleave.native.prevent="continousIncreaseBoxesStop"
                        :disabled="!shipper_price_before_discount">
                        <i class="material-icons">add</i>
                      </d-button>
                    </d-input-group-addon>
                  </d-input-group>

                  <small
                    v-if="notAllProductsHaveCartonsAmount"
                    class="text-danger">
                    <i class="material-icons">report_problem</i> {{ $t('order.notAllProductsHaveItemsInCartonInfoWarning') }}<br>
                  </small>

                  <small v-if="preferredCartonsAmount > 0">
                    {{ $t('order.recommendedCartonsNumber') }}: <span :class="preferredCartonsAmount !== shipper_cartons_number ? 'text-danger' : 'text-success'">{{ preferredCartonsAmount }} {{ $t('ui.piecesAbbr') }}</span>
                    &nbsp;
                    <a
                      v-if="preferredCartonsAmount !== shipper_cartons_number"
                      href="#"
                      @click.prevent="usePreferredCartonsAmount">
                      {{ $t('order.useThisValue') }}
                    </a>
                    <br>
                  </small>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="12" md="4" lg="4">
                  <label>
                    {{ $t('order.trackingNumber') }}:
                  </label>

                  <d-input
                    v-model="tracking_number"
                    type="text"
                    :disabled="trackingNumberIsLocked" />

                  <small
                    v-if="trackingNumberIsLocked"
                    class="text-primary">
                    {{ $t('order.trackingNumberIsLocked') }}
                  </small>
                </d-col>

                <d-col
                  cols="12" md="4" lg="4">
                  <label>
                    {{ $t('order.additionalShippingCost') }}:
                  </label>

                  <validation-provider
                    rules="min_value:0"
                    name="shipper-additional-price"
                    v-slot="{ errors }">
                    <d-input-group>
                      <input
                        :class="{
                          'form-control': true,
                          'is-invalid': errors.length
                        }"
                        :invalid="errors.length ? true : null"
                        :min="0"
                        type="number"
                        :disabled="$refs['order-shipper'].shipper_id === 0 || free_freight"
                        v-model="shipper_additional_price_before_discount"
                        @input="updateShipperAdditionalPrice" />
                      <d-input-group-text slot="append">
                        {{ $refs['order-currency'].selectedCurrencySymbol }}
                      </d-input-group-text>
                    </d-input-group>
                  </validation-provider>

                  <small v-if="shipper_additional_price && roundProperly(shipper_additional_price) !== roundProperly(shipper_additional_price_before_discount)">
                    <label>
                      {{ $t('order.additionalShippingCostAfterDiscount') }}:
                    </label>

                    <span class="text-primary">
                      {{ roundProperly(shipper_additional_price) }} {{ $refs['order-currency'].selectedCurrencySymbol }}
                    </span>
                  </small>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col
                  v-if="isIndentList"
                  cols="4">
                  <label>
                    {{ $t('order.indent.warehouse') }}:
                  </label>
                  <validation-provider
                    rules="required"
                    name="warehouse"
                    v-slot="{ errors }">
                    <d-form-select
                      v-model="warehouse"
                      :state="errors.length ? 'invalid' : null"
                      :options="[
                        { text: $t('order.indent.selectWarehouse'), value: null },
                        { text: 'MG02', value: 'MG02' },
                        { text: 'SE01', value: 'SE01' },
                        { text: 'DE01', value: 'DE01' }
                      ]" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </d-col>
                <d-col
                  :cols="isIndentList ? 4 : 6">
                  <div class="form-group">
                    <label for="description">{{ $t('order.attachmentForWarehouseman') }}:</label>
                    <div>
                      <a
                        v-if="attachment && typeof attachment === 'string'"
                        href="#"
                        @click.prevent="downloadAttachment()">
                        <template v-if="!downloadingAttachment"><i class="material-icons">description</i> {{ $t('attachment.downloadAttachment') }}</template>
                        <template v-if="downloadingAttachment">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                      </a>

                      <a
                        v-if="attachment && typeof attachment === 'string'"
                        href="#"
                        @click.prevent="removeAttachment"
                        class="text-danger ml-3">
                        <small>{{ $t('attachment.removeAttachement') }}</small>
                      </a>

                      <file-input
                        v-if="typeof attachment !== 'string' || !attachment"
                        id="inputAttachment"
                        :label="$t('attachment.selectFileWithAttachmentOptional')"
                        :onChange="setAttachment"></file-input>
                    </div>
                  </div>
                </d-col>

                <d-col
                  :cols="isIndentList ? 4 : 6">
                  <div class="form-group">
                    <label for="description">{{ $t('order.deliveryNote') }}:</label>
                    <div>
                      <a
                        v-if="delivery_note_file && typeof delivery_note_file === 'string'"
                        href="#"
                        @click.prevent="downloadDeliveryNote()">
                        <template v-if="!downloadingDeliveryNote"><i class="material-icons">description</i> {{ $t('attachment.downloadAttachment') }}</template>
                        <template v-if="downloadingDeliveryNote">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                      </a>

                      <a
                        v-if="delivery_note_file && typeof delivery_note_file === 'string'"
                        href="#"
                        @click.prevent="removeDeliveryNote"
                        class="text-danger ml-3">
                        <small>{{ $t('attachment.removeAttachement') }}</small>
                      </a>

                      <file-input
                        v-if="typeof delivery_note_file !== 'string' || !delivery_note_file"
                        id="input2Attachment"
                        :label="$t('attachment.selectFileWithAttachmentOptional')"
                        :onChange="setDeliveryNote"></file-input>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="description">{{ $t('order.warehouse.commentsForWarehouse') }}:</label>
                <d-form-textarea
                  v-model="description_for_warehouse"
                  :value.prop="description_for_warehouse"
                  :placeholder="$t('order.warehouse.addCommentsForWarehouse')"
                  :rows="6" />
              </div>

              <hr>

              <div
                v-if="orderData && orderData.customer_description"
                class="form-group">
                <label for="description">{{ $t('order.commentsForCustomer') }}:</label>

                <p class="text-muted">
                  {{ orderData.customer_description }}
                </p>
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label for="description">{{ $t('order.dataOnInvoice') }}:</label>
                <d-form-textarea
                  v-model="description"
                  :value.prop="description"
                  :placeholder="$t('order.addDataOnInvoice')"
                  :disabled="trackingNumberIsLocked"
                  :rows="6" />
                <small
                  v-if="trackingNumberIsLocked"
                  class="text-primary">
                  {{ $t('order.trackingNumberIsLocked') }}
                </small>
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label for="internal_description">{{ $t('order.additionalInfoInternal') }}:</label>
                <d-form-textarea
                  v-model="internal_description"
                  :value.prop="internal_description"
                  :placeholder="$t('order.addAdditionalInfoInternal')"
                  :rows="6" />
              </div>

              <div
                v-if="orderData && orderData.warehouse_description"
                class="form-group">
                <label for="description">{{ $t('order.commentsFromWarehouse') }}:</label>
                <pre>{{ orderData.warehouse_description }}</pre>
              </div>

              <hr>

              <div
                v-if="!userIsCustomer && isIndentList"
                class="form-group">
                <label for="indent_offer_services_description">{{ $t('order.indentOfferServicesDescription') }}:</label>

                <order-wysiwyg
                  v-if="allDataLoaded"
                  v-model="indent_offer_services_description"
                  key="tinymce-indent-editor" />
              </div>

              <hr v-if="transaction_type === 'EXPORT'">

              <order-ie-files
                v-if="transaction_type === 'EXPORT'"
                :orderID="orderData ? orderData.id : 0"
                :orderFiles="orderData && orderData.ie_files ? orderData.ie_files : []" />

              <hr>

              <order-files
                :orderID="orderData ? orderData.id : 0"
                :orderFiles="orderData ? orderData.files : []"
                :readOnly="false" />

              <hr>

              <div
                v-if="showConnectedOrders && canAddConnectedOrders"
                class="form-group">
                <order-select-connected-orders
                  :customer_id="$refs['order-customer'] && $refs['order-customer'].customer_id" />
              </div>

              <table
                v-if="showConnectedOrders && connected_orders.length"
                class="table table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('ui.orderName') }}</th>
                    <th style="width: 200px;">{{ $t('status.status') }}</th>
                    <th style="width: 80px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(connected_order, index) of connected_orders"
                    :key="'connected-order-' + index">
                    <td>
                      {{ connected_order.order_name }} (#{{ connected_order.id }})
                    </td>
                    <td>
                      <d-badge
                        :theme="getStatusClass(connected_order.status)"
                        :outline="getOutline(connected_order.status)">
                        {{ getStatusName(connected_order.status) }}
                      </d-badge>
                    </td>
                    <td>
                      <d-button
                        theme="danger"
                        size="sm"
                        :disabled="!connected_order.can_delete"
                        @click.prevent="removeConnectedOrder(index)">
                        {{ $t('ui.delete') }}
                      </d-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <d-alert
                v-if="showConnectedOrders && !connected_orders.length"
                show
                theme="light">
                {{ $t('order.noConnectedOrders') }}
              </d-alert>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label>
                  {{ $t('settings.orderTags.orderTags') }}:
                </label>

                <d-alert
                  v-if="!orderTags.length"
                  show
                  theme="light">
                  {{ $t('settings.orderTags.noTagsToDisplay') }}
                </d-alert>

                <ul
                  v-if="orderTags.length"
                  class="order-tags-list">
                  <li
                    v-for="(tag, index) of orderTags"
                    :key="'order-tag-' + index">
                    <d-checkbox
                      inline
                      v-model="tags[tag.id]">
                      <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
                        {{ tag.name }}
                      </d-badge>
                    </d-checkbox>
                  </li>
                </ul>
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label>
                  {{ $t('settings.promotionalTags.promotionalTag') }}:
                </label>

                <div
                  :class="{
                    'form-control': true,
                    'is-select': true
                  }">
                  <v-select
                    class="w-100"
                    :options="promotionalTagsOptions"
                    v-model="promotional_tag_id"
                    :placeholder="$t('settings.promotionalTags.promotionalTag')"
                    :searchable="true"
                    label="text"
                    :reduce="tag => tag.value">
                    <template slot="no-options">
                      {{ $t('settings.promotionalTags.noTagsToDisplay') }}&hellip;
                    </template>
                  </v-select>
                </div>
              </div>
            </d-tab>

            <!-- Hafciarnia -->
            <d-tab
              v-if="userIsAdminOrWorker && orderData && orderData.embriodery_sent_at"
              :title="$t('order.hafciarnia.hafciarnia')">
              <order-hafciarnia
                :embriodery-id="orderData.embriodery_id"
                :embriodery-logs="orderData.embriodery_logs"
                :embriodery-returns="orderData.embroidery_returns"
                :orderID="orderData.id"
                :sync-date="orderData.embriodery_synchronized_at" />
            </d-tab>

            <!-- Zapytanie -->
            <d-tab
              v-if="orderData && orderData.is_inquiry"
              :title="$t('ui.inquiry')">
              <request-creator
                ref="request-creator"
                :readOnly="true"
                :isOrderView="!userIsCustomer"
                :products="products"
                :currencyID="allDataLoaded ? $refs['order-currency'].currency_id : 0"
                :currencies="currencies"
                :services="services"
                :submittedFiles="orderData.files"
                :servicesGroups="servicesGroups"
                :tagCategories="tagCategories" />
            </d-tab>

            <!-- Oferta -->
            <d-tab :title="isOrder ? $t('ui.order') : $t('ui.offer')">
              <fieldset
                v-if="orderData && orderData.customer_order_description"
                class="my-0">
                <legend>
                  <small><small><strong>{{ $t('order.customerOrderDescription') }}:</strong></small></small>
                </legend>
                <p class="my-0">
                  {{ orderData.customer_order_description }}
                </p>
              </fieldset>

              <fieldset
                v-if="orderData && orderData.customer_order_number"
                class="mb-5">
                <legend>
                  <small><small><strong>{{ $t('order.customerOrderNumber') }}:</strong></small></small>
                </legend>
                <p class="text-primary my-0">
                  {{ orderData.customer_order_number }}
                </p>
              </fieldset>

              <order-creator
                ref="order-creator"
                :title="isOrder ? $t('ui.order') : $t('ui.offer') "
                :titleLabel="isOrder ? $t('order.orderDativeCap') : $t('inquiries.offerDativeCap')"
                :currencyId="allDataLoaded ? $refs['order-currency'].currency_id : 0"
                :selectedCurrencySymbol="allDataLoaded ? $refs['order-currency'].selectedCurrencySymbol : ''"
                :shipperPrice="allDataLoaded ? shipper_price : 0"
                :shipperAdditionalPrice="allDataLoaded ? shipper_additional_price : 0"
                :status="status"
                :products="products"
                :indentProducts="indentProducts"
                :services="services"
                :isEditMode="isEditMode"
                :isInquiryMode="isInquiryMode"
                :isOrder="isOrder"
                :isIndent="isIndentList"
                :isVirtual="is_virtual"
                :orderSetUpInProgress="orderSetUpInProgress"
                :includeSetupPrice="include_setup_prices"
                :datesNotRequired="dates_not_required"
                :customerProductsDiscount="allDataLoaded && include_discounts ? prepareDiscount(discount_products) : 0"
                :customerServicesDiscount="allDataLoaded && include_discounts ? prepareDiscount(discount_services) : 0"
                :warnings="orderData && orderData.warnings ? orderData.warnings : []"
                :collectAlert="orderData && !!orderData.collect_alert"
                :collectWarning="orderData && !!orderData.collect_warning"
                :contractors="contractors"
                :canAddDestroyedItemsInfo="canAddDestroyedItemsInfo"
                :tagCategories="tagCategories"
                :embroideryItems="embroideryItems"
                :deadline="deadline"
                :orderPurchases="orderData && orderData.purchases"
                :isOffer="isOffer"
                :skonto="skonto" />

              <hr v-if="!isOrder || isInquiryMode">

              <div key="offer-for-each-product">
                <d-checkbox
                  v-if="!isOrder || isInquiryMode"
                  inline
                  v-model="offer_for_each_product"
                  toggle
                  toggle-small
                  :disabled="attach_service_prices">
                  {{ $t('product.breakProductsIntoSeparateOffers') }}
                </d-checkbox>
              </div>

              <hr v-if="!isOrder || isInquiryMode">

              <div
                v-if="!isOrder || isInquiryMode"
                :class="{ 'attach-service-prices': true }">
                <d-checkbox
                  inline
                  v-model="attach_service_prices"
                  toggle
                  toggle-small>
                  {{ $t('service.attachSelectdServicesPricesLists') }}
                </d-checkbox>

                <d-row
                  v-if="attach_service_prices"
                  class="mt-2">
                  <d-col cols="4">
                    <d-input
                      v-model="attached_services_filter"
                      :placeholder="$t('service.filterServicesByName')" />
                  </d-col>
                  <d-col cols="8" class="d-flex">
                    <d-button
                      outline
                      class="ml-auto"
                      :disabled="attached_services_filter.trim() !== ''"
                      @click.prevent="selectAllAttachedServices">
                      {{ $t('ui.selectAll') }}
                    </d-button>

                    <d-button
                      outline
                      class="ml-4"
                      :disabled="attached_services_filter.trim() !== ''"
                      @click.prevent="unselectAllAttachedServices">
                      {{ $t('ui.deselectAll') }}
                    </d-button>
                  </d-col>
                </d-row>

                <ul
                  v-if="attach_service_prices"
                  class="attach-service-prices-list">
                  <li
                    v-for="(service, key, index) of attached_services"
                    :key="'service-to-attach-' + index"
                    v-show="attached_services_filter.trim() === '' || getServiceNameByID(key).toLocaleLowerCase().indexOf(attached_services_filter.toLocaleLowerCase()) > -1">
                    <d-checkbox
                      inline
                      v-model="attached_services[key]"
                      toggle
                      toggle-small>
                      {{ getServiceNameByID(key) }}
                    </d-checkbox>
                  </li>
                </ul>
              </div>

              <hr v-if="!isOrder || isInquiryMode">

              <div
                v-if="!isOrder || isInquiryMode"
                :class="{ 'inquiry-range-services': true, 'is-disabled': offer_for_each_product }">
                <d-checkbox
                  inline
                  v-model="inquiry_range_services"
                  toggle
                  toggle-small
                  :disabled="offer_for_each_product">
                  {{ $t('inquiries.inquiryForProductsNumberRange') }}
                </d-checkbox>

                <d-row
                  v-if="inquiry_range_services"
                  class="mt-2 align-middle d-flex flex-align-middle">
                  <span class="pt-1 pl-3">{{ $t('ui.range') }}:</span>
                  <d-col cols="2">
                    <d-input
                      v-model="services_range_from"
                      type="number"
                      placeholder="" />
                  </d-col>
                  <span class="pt-1"> - </span>
                  <d-col cols="2">
                    <d-input
                      v-model="services_range_to"
                      type="number"
                      placeholder="" />
                  </d-col>
                </d-row>
              </div>

              <hr v-if="!isOrder || isInquiryMode">

              <div
                v-if="!isOrder || isInquiryMode"
                :class="{ 'inquiry-range-services': true, 'is-disabled': offer_for_each_product }">
                <d-checkbox
                  inline
                  v-model="join_products_in_prices"
                  toggle
                  toggle-small
                  :disabled="offer_for_each_product">
                  {{ $t('inquiries.combineModelInQuote') }}
                </d-checkbox>
              </div>
            </d-tab>

            <d-tab
              v-if="isIndentList"
              :title="$t('indentRegistry.indent')">
              <h4
                v-if="isEditMode"
                class="mb-3 pb-3 d-flex">
                {{ $t('indentRegistry.indent') }}
              </h4>

              <d-alert
                v-if="!isEditMode"
                show
                theme="warning">
                {{ $t('indentRegistry.orderIndentDatawillBeAvailableAfterSave') }}
              </d-alert>

              <d-alert
                v-if="isEditMode"
                show
                theme="primary"
                class="text-center">
                {{ $t('indentRegistry.theseInformationsWillBeUpdatedAfterSave') }}
              </d-alert>

              <d-card
                v-if="isEditMode"
                class="p-4 mb-4">
                <p class="my-1">HSP ORDER NO: <span class="text-primary">{{ orderData.order_name }}</span></p>
                <p class="my-1">HSP ORDER DATE: <span class="text-primary">{{ orderData.created_at }}</span></p>
                <p class="my-1">
                  EARLIEST SHIPDATE:
                  <span
                    v-if="earliestShipDate"
                    class="text-primary">
                    {{ earliestShipDate }}
                  </span>
                </p>
              </d-card>

              <d-card
                v-if="isEditMode"
                class="mb-4 py-4 px-4">
                <d-row>
                  <d-col cols="4">
                    <label>
                      {{ $t('indentRegistry.indentRegistry') }}
                    </label>

                    <d-form-select
                      v-model="indent_registry"
                      :options="[
                        {
                          value: null,
                          text: $t('indentRegistry.selectRegistry')
                        },
                        {
                          text: 'HSE',
                          value: 'HSE'
                        },
                        {
                          text: 'HPL',
                          value: 'HPL'
                        },
                        {
                          text: 'HBE',
                          value: 'HBE'
                        },
                        {
                          text: 'STOCK',
                          value: 'STOCK'
                        },
                        {
                          text: 'HGER',
                          value: 'HGER'
                        }
                      ]">
                    </d-form-select>
                  </d-col>

                  <d-col cols="4">
                    <label>
                      {{ $t('indentRegistry.customerOrderName') }}
                    </label>

                    <d-input
                      v-model="customer_order_name"
                      type="text" />
                  </d-col>

                  <d-col cols="4">
                    <label>
                      {{ $t('indentRegistry.customerDesignName') }}
                    </label>

                    <d-input
                      v-model="customer_design_name"
                      type="text" />
                  </d-col>
                </d-row>

                <d-row class="mt-3">
                  <d-col cols="4">
                    <label class="d-flex">
                      <span>
                        {{ $t('indentRegistry.customerOrderDate') }}
                      </span>

                      <a
                        v-if="customer_order_date"
                        href="#"
                        class="ml-auto text-danger"
                        @click.prevent="removeDate('customer_order_date')">
                        <small>
                          {{ $t('order.removeDate')}}
                        </small>
                      </a>
                    </label>

                    <d-datepicker
                      v-model="customer_order_date"
                      :monday-first="true"
                      format="yyyy-MM-dd"
                      :placeholder="$t('ui.chooseDate')"
                      :language="translationsForDatePicker" />
                  </d-col>

                  <d-col cols="4">
                    <label>
                      {{ $t('indentRegistry.freightType') }}
                    </label>

                    <d-form-select
                      v-model="freight_type"
                      :options="[
                        {
                          value: null,
                          text: $t('indentRegistry.selectFreightType')
                        },
                        {
                          text: 'SEA',
                          value: 'SEA'
                        },
                        {
                          text: 'AIR',
                          value: 'AIR'
                        },
                        {
                          text: 'SEA/AIR',
                          value: 'SEA/AIR'
                        },
                        {
                          text: 'FASTTRACK',
                          value: 'FASTTRACK'
                        }
                      ]">
                    </d-form-select>
                  </d-col>

                  <d-col cols="4">
                    <label class="d-flex">
                      <span>
                        {{ $t('indentRegistry.originalShipmentDate') }}
                      </span>

                      <a
                        v-if="original_shipment_date"
                        href="#"
                        class="ml-auto text-danger"
                        @click.prevent="removeDate('original_shipment_date')">
                        <small>
                          {{ $t('order.removeDate')}}
                        </small>
                      </a>
                    </label>

                    <d-datepicker
                      v-model="original_shipment_date"
                      :monday-first="true"
                      format="yyyy-MM-dd"
                      :placeholder="$t('ui.chooseDate')"
                      :language="translationsForDatePicker" />
                  </d-col>
                </d-row>
              </d-card>

              <d-card
                v-if="isIndentList && isEditMode"
                class="p-4">
                <h5 class="mb-3 pb-3 d-flex">
                  {{ $t('indentRegistry.products') }}
                </h5>

                <d-card
                  v-for="(indentProduct, index) of orderData.products"
                  :key="'indent-product-list-' + index"
                  class="p-3 mb-4">
                  <d-row>
                    <d-col cols="6">
                      {{ $t('indentRegistry.code') }}:
                      <span class="text-primary">{{ indentProduct.code }}</span><br>
                      {{ $t('indentRegistry.quantity') }}:
                      <span class="text-primary">{{ indentProduct.quantity }} {{ $t('ui.piecesAbbr') }}</span><br>
                      {{ $t('indentRegistry.cnCode') }}:
                      <span class="text-primary">{{ indentProduct.cn_code }}</span>
                    </d-col>

                    <d-col cols="6">
                      {{ $t('indentRegistry.serviceObtainingAt') }}:
                      <span
                        v-if="indentProduct.service_obtaining_at"
                        class="text-primary"
                        :key="'if-obtaining-at-' + index">
                        {{ indentProduct.service_obtaining_at }}
                      </span>
                      <span
                        v-else
                        class="text-gray"
                        :key="'else-obtaining-at-' + index">
                        &ndash;
                      </span>
                      <br>
                      {{ $t('indentRegistry.sampleProductionDays') }}:
                      <span
                        v-if="indentProduct.sample_production_days"
                        class="text-primary"
                        :key="'if-production-days-' + index">
                        {{ indentProduct.sample_production_days }}
                      </span>
                      <span
                        v-else
                        class="text-gray"
                        :key="'else-production-days-' + index">
                        &ndash;
                      </span>
                      <br>
                      {{ $t('indentRegistry.serviceAcceptedAt') }}:
                      <span
                        v-if="indentProduct.service_accepted_at"
                        class="text-primary"
                        :key="'if-accepted-at-' + index">
                        {{ indentProduct.service_accepted_at }}
                      </span>
                      <span
                        v-else
                        class="text-gray"
                        :key="'else-accepted-at-' + index">
                        &ndash;
                      </span>
                      <br>
                      {{ $t('indentRegistry.timeframeProduction') }}:
                      <span
                        v-if="indentProduct.timeframe_production"
                        class="text-primary"
                        :key="'if-timeframe-production-' + index">
                        {{ indentProduct.timeframe_production }}
                      </span>
                      <span
                        v-else
                        class="text-gray"
                        :key="'else-timeframe-production-' + index">
                        &ndash;
                      </span>
                    </d-col>
                  </d-row>
                </d-card>
              </d-card>
            </d-tab>

            <!-- Kontakty z klientem -->
            <d-tab
              v-if="!userIsCustomer"
              :title="$t('customer.contactsWithCustomer')">
              <order-contacts
                v-if="isEditMode"
                :orderID="parseInt(this.$route.params.id, 10)"
                :isOrderView="true" />
              <p
                v-if="!isEditMode"
                class="text-center">
                {{ $t('customer.customerContactsInfoAvailableAfterSave') }}&hellip;
              </p>
            </d-tab>

            <!-- Reklamacje -->
            <d-tab
              v-if="!userIsCustomer"
              :title="$t('complaints.complaints')">
              <order-complaints
                v-if="isEditMode"
                :orderID="parseInt(this.$route.params.id, 10)"
                :isOrderView="true" />
              <p
                v-if="!isEditMode"
                class="text-center">
                {{ $t('complaints.complaintsInfoAvailableAfterSave') }}&hellip;
              </p>
            </d-tab>

            <!-- Przesyłki -->
            <d-tab
              v-if="userIsAdminOrWorker && $showUpsIntegration"
              :title="$t('shipments.shipments')">
              <order-shipments
                v-if="isEditMode"
                :orderID="parseInt(this.$route.params.id, 10)"
                :orderName="orderData.order_name"
                :addressData="shipmentAddressData"
                :contactData="shipmentContactData"
                :customerID="$refs['order-customer'] ? $refs['order-customer'].customer_id : null"
                :customerData="customerShipmentData"
                :customerContactData="customerContactData"
                :orderDescription="description" />
              <p
                v-if="!isEditMode"
                class="text-center">
                {{ $t('shipments.shipmentsAvailableAfterSave') }}&hellip;
              </p>
            </d-tab>

            <!-- PUESC -->
            <d-tab
              v-if="userIsAdminOrWorker"
              :title="$t('order.puesc.title')">
              <order-puesc
                :orderID="parseInt(this.$route.params.id, 10)"
                :addressData="shipmentAddressData"
                :countries="countries"
                :customerData="orderData ? orderData.customer : {}"
                :puescData="orderData ? orderData.puesc_information : {}" />
            </d-tab>

            <!-- Historia -->
            <d-tab :title="$t('order.statusHistory')">
              <p
                v-if="!isEditMode"
                class="text-center">
                {{ $t('customer.historyInfoAvailableAfterSave') }}&hellip;
              </p>

              <order-history
                v-if="isEditMode"
                :statuses="statuses" />
            </d-tab>

            <!-- Ewidencja -->
            <d-tab :title="$t('order.evidence')">
              <p
                v-if="!isEditMode"
                class="text-center">
                {{ $t('order.evidenceInfoAvailableAfterSave') }}&hellip;
              </p>

              <order-evidence
                v-if="isEditMode"
                :orderID="this.orderData.id" />
            </d-tab>

            <!-- Powiązane faktury -->
            <d-tab :title="$t('order.connectedInvoices')">
              <p
                v-if="!(isOrder || isOrderMode)"
                class="text-center">
                {{ $t('order.invoicesInfoAvailableWhenOfferIsInquiry') }}&hellip;
              </p>

              <order-settlements
                v-if="isEditMode && (isOrder || isOrderMode) && allDataLoaded"
                :settlementsData="settlements"
                :orderName="order_name"
                :orderID="orderData.id"
                :customerID="this.$refs['order-customer'].customer_id" />
            </d-tab>

            <d-tab
              v-if="isEditMode"
              :title="$t('order.remindersList')">
              <p
                v-if="!emails.length"
                class="text-center">
                {{ $t('order.noRemindersSent') }}&hellip;
              </p>

              <order-reminders
                v-if="emails.length"
                :emails="emails" />
            </d-tab>

            <d-tab
              v-if="isEditMode"
              :title="$t('notifications.notificationsLabel')">
              <notifications
                :isOrderView="true" />
            </d-tab>

            <!-- Kartony -->
            <d-tab
              v-if="isEditMode && userIsAdminOrWorker && orderData.boxes_accepted_at"
              :title="$t('order.warehouse.boxes.title')">
              <order-collect-boxes
                v-if="$showWarehouseBoxesUI"
                :deliveryNoteConfirmedAt="orderData.delivery_note_confirmed_at"
                :deliveryNoteConfirmedBy="orderData.delivery_note_confirmed_by"
                :orderID="orderData.id"
                :orderProducts="orderData.products"
                :boxesAcceptedAt="orderData.boxes_accepted_at"
                mode="order" />

              <order-returns
                :returns="orderData.embroidery_returns" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded || orderSetUpInProgress" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <customer-contact-person-new-popup
      v-if="showContactPersonAddPopup"
      :customerID="this.$refs['order-customer'].customer_id" />

    <customer-address-new-popup
      v-if="showAddressAddPopup"
      :countries="countries"
      :defaultEmail="newAddressDefaultEmail"
      :customerID="this.$refs['order-customer'].customer_id" />

    <customer-address-edit-popup
      v-if="showAddressEditPopup"
      :customerID="this.$refs['order-customer'].customer_id"
      :countries="countries"
      :addressData="selectedAddressData" />

    <customer-address-view-popup
      v-if="showAddressViewPopup"
      :countries="countries"
      :addressData="selectedAddressData"
      :customerID="this.$refs['order-customer'].customer_id"
      :showEditButton="true" />

    <customer-contact-person-view-popup
      v-if="showContactPersonViewPopup"
      :personData="selectedPersonData" />

    <container-search-popup
      v-if="showContainerSearchPopup"
      :productID="selectedContainerProduct"
      :orderProductID="selectedContainerOrderProduct"
      :missingAmount="missingProductAmount"
      :usedContainers="usedContainers"
      :isOffer="containerSearchPopupOfferMode" />

    <purchase-search-popup
      v-if="showPurchaseSearchPopup"
      :productID="selectedPurchaseProduct"
      :purchaseID="selectedPurchaseID"
      :orderProductID="selectedPurchaseOrderProduct"
      :missingAmount="missingProductAmount" />

    <purchase-add-popup
      v-if="showPurchaseAddPopup"
      :orderID="parseInt(this.$route.params.id, 10)"
      :productID="selectedPurchaseProduct"
      :products="products"
      :purchaseID="selectedPurchaseID"
      :orderProductID="selectedPurchaseOrderProduct"
      :customerID="selectedPurchaseProductCustomerID"
      :subCustomerID="selectedPurchaseProductSubCustomerID"
      :missingAmount="missingProductAmount" />

    <purchase-edit-popup
      v-if="showPurchaseEditPopup"
      :productID="selectedPurchaseProduct"
      :purchaseProductID="selectedProductInPurchase"
      :orderProductID="selectedPurchaseOrderProduct"
      :additionalData="selectedPurchaseAdditionalData"
      :purchaseData="selectedPurchaseData" />

    <customer-discounts-popup
      v-if="showCustomerDiscountsPopup"
      :selected-customer="$refs['order-customer'].selectedCustomer" />

    <contact-popup
      v-if="showOrderContactPopup"
      :orderID="parseInt(this.$route.params.id, 10)"
      :customerID="$refs['order-customer'].customer_id" />

    <contact-edit-popup
      v-if="showOrderContactEditPopup"
      :contactID="selectedContactID" />

    <complaint-new-popup
      v-if="isEditMode && userIsAdminOrWorker && showComplaintNewPopup"
      :orderID="parseInt(this.$route.params.id, 10)"
      :products="orderData && orderData.products"
      :services="orderData && orderData.services" />

    <order-name-popup
      v-if="displayOrderNamePopup"
      :ordersData="foundedOrderNames" />

    <order-price-changes-popup
      v-if="displayPriceChangesPopup"
      :changedProductPrices="changedProductPrices"
      :changedProductServicePrices="changedProductServicePrices"
      :changedGlobalServicePrices="changedGlobalServicePrices" />

    <invoice-diff-popup
      v-if="displayInvoiceDiffPopup"
      :invoiceDiffData="invoiceDiffData" />

    <add-indent-product-popup
      v-if="displayAddIndentProductPopup"
      :selectedProductIndex="selectedIndentProductIndexToAdd" />

    <order-notification-new-popup
      v-if="displayNewNotificationPopup"
      :orderID="parseInt(this.$route.params.id, 10)" />

    <order-erp-integration-popup
      v-if="showErpIntegrationPopup"
      :orderID="parseInt(this.$route.params.id, 10)" />

    <order-confirmations-popup
      v-if="showConfirmationsPopup"
      :confirmations="loadedConfirmations" />

    <order-gus-comparision-popup
      v-if="showGusComparisionPopup"
      :orderSavedCustomerID="orderData ? orderData.customer_id : 0"
      :dataForComparision="receivedGusData" />
  </d-container>
</template>

<script>
import AddIndentProductPopup from './AddIndentProductPopup.vue';
import ComplaintNewPopup from './../../components/complaints/ComplaintNewPopup.vue';
import Complaints from './../../views/Complaints.vue';
import Contacts from './../../views/Contacts.vue';
import ContainerSearchPopup from './../orders/ContainerSearchPopup.vue';
import CustomerContactPersonNewPopup from './../customers/CustomerContactPersonNewPopup.vue';
import CustomerAddressEditPopup from '../customers/CustomerAddressEditPopup.vue';
import CustomerAddressNewPopup from './../customers/CustomerAddressNewPopup.vue';
import CustomerAddressViewPopup from './../customers/CustomerAddressViewPopup.vue';
import CustomerContactPersonViewPopup from './../customers/CustomerContactPersonViewPopup.vue';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import CustomerDiscountsPopup from './../orders/CustomerDiscountsPopup.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import InvoiceDiffPopup from './InvoiceDiffPopup.vue';
import ListUtils from './../../utils/ListUtils.js';
import ContactPopup from './../contacts/ContactPopup.vue';
import ContactEditPopup from './../contacts/ContactEditPopup.vue';
import Notifications from '../../views/Notifications.vue';
import OrderCollectBoxes from './OrderCollectBoxes.vue';
import OrderConfirmationsPopup from './OrderConfirmationsPopup.vue';
import OrderCreator from './OrderCreator.vue';
import OrderErpIntegrationPopup from './OrderErpIntegrationPopup.vue';
import OrderEvidence from './OrderEvidence.vue';
import OrderFiles from './OrderFiles.vue';
import OrderGusComparisionPopup from './OrderGusComparisionPopup.vue';
import OrderIEFiles from './OrderIEFiles.vue';
import OrderHistory from './OrderHistory.vue';
import OrderNamePopup from './OrderNamePopup.vue';
import OrderNotificationNewPopup from './OrderNotificationNewPopup.vue';
import OrderPriceChangePopup from './OrderPriceChangePopup.vue';
import OrderPuesc from './OrderPuesc.vue';
import OrderReminders from './OrderReminders.vue';
import OrderReturns from './OrderReturns.vue';
import OrderSelectAddress from './OrderSelectAddress.vue';
import OrderSelectBrand from './OrderSelectBrand.vue';
import OrderSelectConnectedOrders from './OrderSelectConnectedOrders.vue';
import OrderSelectContact from './OrderSelectContact.vue';
import OrderSelectCurrency from './OrderSelectCurrency.vue';
import OrderSelectCustomer from './OrderSelectCustomer.vue';
import OrderSelectLang from './OrderSelectLang.vue';
import OrderSelectShipper from './OrderSelectShipper.vue';
import OrderSelectTime from './OrderSelectTime.vue';
import OrderSelectWorker from './OrderSelectWorker.vue';
import OrderSettlements from './OrderSettlements.vue';
import OrderShipments from './OrderShipments.vue';
import OrderStatus from './OrderStatus.vue';
import orderStatuses from './../../data/order-statuses.js';
import orderStatusClasses from './../../data/order-status-classes.js';
import orderStatusPhase from './../../data/order-status-phase.js';
import OrderTaxNumberChecker from './OrderTaxNumberChecker.vue';
import OrderWysiwyg from './OrderWysiwyg.vue';
import PurchaseSearchPopup from './../orders/PurchaseSearchPopup.vue';
import PurchaseAddPopup from './../orders/PurchaseAddPopup.vue';
import PurchaseEditPopup from './../orders/PurchaseEditPopup.vue';
import RequestCreator from './RequestCreator.vue';
import OrderHafciarnia from './OrderHafciarnia.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'order',
  mixins: [
    CustomerPanel,
    OrderTaxNumberChecker
  ],
  props: [
    'orderData',
    'isLocked',
    'isLoading'
  ],
  components: {
    'add-indent-product-popup': AddIndentProductPopup,
    'complaint-new-popup': ComplaintNewPopup,
    'container-search-popup': ContainerSearchPopup,
    'customer-contact-person-new-popup': CustomerContactPersonNewPopup,
    'customer-address-edit-popup': CustomerAddressEditPopup,
    'customer-address-new-popup': CustomerAddressNewPopup,
    'customer-address-view-popup': CustomerAddressViewPopup,
    'customer-contact-person-view-popup': CustomerContactPersonViewPopup,
    'contact-popup': ContactPopup,
    'contact-edit-popup': ContactEditPopup,
    'invoice-diff-popup': InvoiceDiffPopup,
    'notifications': Notifications,
    'order-collect-boxes': OrderCollectBoxes,
    'order-complaints': Complaints,
    'order-confirmations-popup': OrderConfirmationsPopup,
    'order-contacts': Contacts,
    'order-creator': OrderCreator,
    'order-erp-integration-popup': OrderErpIntegrationPopup,
    'order-evidence': OrderEvidence,
    'order-files': OrderFiles,
    'order-gus-comparision-popup': OrderGusComparisionPopup,
    'order-ie-files': OrderIEFiles,
    'order-history': OrderHistory,
    'order-name-popup': OrderNamePopup,
    'order-notification-new-popup': OrderNotificationNewPopup,
    'order-price-changes-popup': OrderPriceChangePopup,
    'order-puesc': OrderPuesc,
    'order-reminders': OrderReminders,
    'order-returns': OrderReturns,
    'order-select-address': OrderSelectAddress,
    'order-select-brand': OrderSelectBrand,
    'order-select-connected-orders': OrderSelectConnectedOrders,
    'order-select-contact': OrderSelectContact,
    'order-select-currency': OrderSelectCurrency,
    'order-select-customer': OrderSelectCustomer,
    'order-select-lang': OrderSelectLang,
    'order-select-shipper': OrderSelectShipper,
    'order-select-time': OrderSelectTime,
    'order-select-worker': OrderSelectWorker,
    'order-settlements': OrderSettlements,
    'order-shipments': OrderShipments,
    'order-status': OrderStatus,
    'order-wysiwyg': OrderWysiwyg,
    'purchase-search-popup': PurchaseSearchPopup,
    'purchase-add-popup': PurchaseAddPopup,
    'purchase-edit-popup': PurchaseEditPopup,
    'request-creator': RequestCreator,
    'customer-discounts-popup': CustomerDiscountsPopup,
    'order-hafciarnia': OrderHafciarnia
  },
  computed: {
    isIndentList () {
      return this.$route.path.indexOf('/zamowienia-indent') > -1 || this.$route.path.indexOf('/oferty-indent') > -1;
    },
    showBlockadeButton () {
      return !this.showBlockadeBlock && !this.hasExcludedStatusForBlockade;
    },
    showCustomerApprovalButton () {
      return this.userIsAdminOrWorker && this.isEditMode && (this.isInquiryMode || this.isOffer);
    },
    showFollowUpButton () {
      return this.userIsAdminOrWorker && this.isEditMode && (this.isOffer && this.orderData.can_send_followup);
    },
    showSendToHafciarniaButton () {
      return this.$showHafciarniaIntegration && this.userIsAdminOrWorker && this.isEditMode && !this.isOffer && this.orderData.can_send_to_embriodery;
    },
    showSendAgainToHafciarniaButton () {
      return this.$showHafciarniaIntegration && this.userIsAdminOrWorker && this.isEditMode && !this.isOffer && this.orderData.can_send_again_to_embroidery;
    },
    backUrl () {
      let allowedBackUrls = [
        '/kontakty-z-klientami',
        '/kontenery/rezerwacje',
        '/oferty',
        '/oferty-indent',
        '/przesylki',
        '/reklamacje',
        '/zakupy/rezerwacje',
        '/zamowienia',
        '/zamowienia-indent',
        '/zamowienia-hafciarnia',
        '/zapytania',
        '/zamowienia-partnerow'
      ];

      if (sessionStorage.getItem('headwear-crm-previous-view') && allowedBackUrls.indexOf(sessionStorage.getItem('headwear-crm-previous-view')) > -1) {
        return sessionStorage.getItem('headwear-crm-previous-view');
      }

      if (this.$route.path.startsWith('/zamowienia-hafciarnia/')) {
        return '/zamowienia-hafciarnia';
      }

      if (this.$route.params.fromreservations === 'z-rezerwacji') {
        return '/kontenery/rezerwacje';
      } else if (this.$route.params.fromreservations === 'z-rezerwacji-zakupow') {
        return '/zakupy/rezerwacje';
      } else if (
        this.orderData &&
        this.orderData.is_inquiry &&
        this.isInquiryMode &&
        this.orderData.status !== 'SENT_TO_CUSTOMER' &&
        this.orderData.status !== 'OFFER_CLOSED'
      ) {
        return '/zapytania';
      } else if (
        this.orderData &&
        this.orderData.status === 'CUSTOMER_ORDER'
      ) {
        return '/zamowienia-partnerow';
      } else if (
        (this.isOrderMode || this.isOrder) && (
          !this.orderData || (
            this.orderData.status !== 'SENT_TO_CUSTOMER' &&
            this.orderData.status !== 'OFFER_CLOSED'
          )
        )
      ) {
        if (this.isIndentList) {
          return '/zamowienia-indent';
        }

        return '/zamowienia';
      }

      return '/oferty';
    },
    canSendInvoice () {
      return this.canSendSettlementInvoice;
    },
    canSendSettlementInvoice () {
      return orderStatusPhase['send_invoice'].indexOf(this.status) > -1;
    },
    canAddConnectedOrders () {
      return orderStatusPhase['able_to_connect'].indexOf(this.status) > -1;
    },
    canAddDestroyedItemsInfo () {
      return orderStatusPhase['able_to_log_destroyed_items'].indexOf(this.status) > -1
    },
    isEditMode () {
      return !!this.$route.params.id;
    },
    isOrderMode () {
      return this.$route.path === '/zamowienia/nowe' || this.$route.path === '/zamowienia/nowe/' || this.$route.path === '/zamowienia-indent/nowe' || this.$route.path === '/zamowienia-indent/nowe/';
    },
    isInquiryMode () {
      return this.$route.path.indexOf('/zapytania/zapytanie/') > -1;
    },
    isOrder () {
      if (this.isOrderMode) {
        return true;
      }

      if (!this.orderData) {
        return false;
      }

      return this.orderData.status !== 'SENT_TO_CUSTOMER' && this.orderData.status !== 'NEW';
    },
    isOffer () {
      if (this.$route.path.indexOf('/oferty-indent/nowa') > -1 || this.$route.path.indexOf('/oferty/nowa') > -1) {
        return true;
      }

      if (!this.orderData) {
        return false;
      }

      return this.orderData.status === 'SENT_TO_CUSTOMER' || this.orderData.status === 'NEW';
    },
    allDataLoaded () {
      if (!this.isEditMode && this.isIndentList && this.isOffer) {
        return this.loadedData.join(',') === 'brands,contractors,countries,currencies,embroidery-items,indent-products,langs,order-tags,products,promotional-tags,services,services-groups,shippers,tag-categories,times,user-tags';
      }

      if (this.isIndentList && this.isOffer) {
        return this.loadedData.join(',') === 'brands,contractors,countries,currencies,customer,embroidery-items,indent-products,langs,order-tags,products,promotional-tags,services,services-groups,shippers,tag-categories,times';
      }

      if (this.isEditMode) {
        return this.loadedData.join(',') === 'brands,contractors,countries,currencies,customer,embroidery-items,langs,order-tags,products,promotional-tags,services,services-groups,shippers,tag-categories,times';
      }

      return this.loadedData.join(',') === 'brands,contractors,countries,currencies,embroidery-items,langs,order-tags,products,promotional-tags,services,services-groups,shippers,tag-categories,times,user-tags';
    },
    realizationDate () {
      let date = this.orderData.realization_date;

      if (!date) {
        return this.$t('ui.lack');
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    showConnectedOrders () {
      return this.isEditMode;
    },
    hasExcludedStatusForBlockade () {
      let excludedStatuses = ['TO_COLLECT', 'ENDED', 'TO_DELETE', 'DELETED', 'OFFER_CLOSED'];

      if (excludedStatuses.indexOf(this.status) > -1) {
        return true;
      }

      return false;
    },
    showBlockadeBlock () {
      if (this.hasExcludedStatusForBlockade) {
        return false;
      }

      return this.blockade_status || this.configureBlockade || this.blocked_at || this.blockade_returned_at || this.blockade_returned_by;
    },
    validationResults () {
      let errors = this.$refs['order-form'].errors;
      let allKeys = Object.keys(errors);
      let errorKeys = [];
      let errorTabs = [];
      let results = [
        ['error-1', ['order_name', 'contractor_id', 'customer_id', 'contact_id', 'advance_percent', 'address_id', 'brand', 'shipper_id', 'processing_time_id', 'currency_id', 'language_id', 'shipper-price-before-discount', 'discount_products', 'discount_services', 'discount_shipping', 'transaction_type', 'warehouse']],
        ['error-2', ['product-id-', 'product-', 'product-amount-', 'service-id-', 'service-price', 'global-service-id-', 'global-service-price']]
      ];

      for (let i = 0; i < allKeys.length; i++) {
        if (errors[allKeys[i]].length) {
          errorKeys.push(allKeys[i]);
        }
      }

      errorKeys = errorKeys.join(',');

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errorKeys.indexOf(key) > -1) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    discountsState () {
      return [this.discount_products, this.discount_services, this.discount_shipping].join('-');
    },
    hasDifferentDiscounts () {
      console.log('Current discounts:', this.discountsState);

      if (
        this.orderSetUpInProgress ||
        !this.$refs['order-customer'] ||
        !this.$refs['order-customer'].selectedCustomer
      ) {
        return false;
      }

      if (this.prepareDiscount(this.discount_products) !== parseFloat(this.$refs['order-customer'].selectedCustomer.discount_products)) {
        return true;
      }

      if (this.prepareDiscount(this.discount_services) !== parseFloat(this.$refs['order-customer'].selectedCustomer.discount_services)) {
        return true;
      }

      if (this.prepareDiscount(this.discount_shipping) !== parseFloat(this.$refs['order-customer'].selectedCustomer.discount_shipping)) {
        return true;
      }

      return false;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    userIsAdminOrWorker () {
      return this.$store.getters.getUserType === 'admin' || this.$store.getters.getUserType === 'worker';
    },
    hasAdvanceIssue () {
      return this.userIsAdminOrWorker && this.is_advance && !this.advance_paid;
    },
    canResetInvoiceRequest () {
      if (this.orderData) {
        return this.orderData.can_reset_request_invoice;
      }

      return false;
    },
    invoiceRequestInProgress () {
      if (this.orderData) {
        return this.orderData.invoice_request || this.orderData.invoice_request_by_user || this.invoiceRequestSent;
      }

      return this.invoiceRequestSent;
    },
    pastDates () {
      return {
        to: new Date()
      };
    },
    earliestShipDate () {
      if (this.shipper_date) {
        return [this.shipper_date.getFullYear(), this.shipper_date.getMonth() + 1, this.shipper_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      }

      return '-';
    },
    previousCurrencySymbol () {
      if (!this.previousCurrency) {
        return 'x';
      }

      let previousCurrencyData = this.currencies.filter(currency => currency.id === this.previousCurrency);

      if (!previousCurrencyData || !previousCurrencyData.length) {
        return 'y';
      }

      return previousCurrencyData[0].code;
    },
    invoiceCountryPrefixIsVerified () {
      if (!this.isEditMode) {
        return false;
      }

      if (!this.orderData || !this.orderData.customer) {
        return false;
      }

      let countryPrefix = this.orderData.invoice_country_prefix;
      let country = this.countries.filter(countryItem => countryItem.code === countryPrefix);

      if (country && country[0]) {
        return !!country[0].verify_tax_number;
      }

      return false;
    },
    containerOptions () {
      let options = [];

      for (let i = 0; i < this.containers.length; i++) {
        let option = {
          text: this.containers[i].number,
          value: this.containers[i].id,
          date: this.containers[i].date
        };

        options.push(option);
      }

      return options;
    },
    promotionalTagsOptions () {
      return this.promotionalTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    polandCountryID () {
      let countryPL = this.countries.filter(country => country.code === 'PL');

      if (countryPL[0]) {
        return countryPL[0].id;
      }

      return false;
    },
    noInvoiceRequests () {
      if (this.orderData) {
        return this.orderData.invoice_request === 0;
      }

      return false;
    }
  },
  watch: {
    loadedCountries () {
      this.$bus.$emit('order-data-fetch-success', 'countries');
    },
    loadedCurrencies () {
      this.$bus.$emit('order-data-fetch-success', 'currencies');
    },
    loadedContractors () {
      this.$bus.$emit('order-data-fetch-success', 'contractors');
    },
    loadedEmbroideryItems () {
      this.$bus.$emit('order-data-fetch-success', 'embroidery-items');
    },
    loadedProducts () {
      this.$bus.$emit('order-data-fetch-success', 'products');
    },
    loadedIndentProducts () {
      this.$bus.$emit('order-data-fetch-success', 'indent-products');
    },
    loadedServices () {
      this.$bus.$emit('order-data-fetch-success', 'services');
    },
    loadedServicesGroups () {
      this.$bus.$emit('order-data-fetch-success', 'services-groups');
    },
    loadedTagCategories () {
      this.$bus.$emit('order-data-fetch-success', 'tag-categories');
    },
    loadedOrderTags () {
      this.$bus.$emit('order-data-fetch-success', 'order-tags');
    },
    loadedPromotionalTags () {
      this.$bus.$emit('order-data-fetch-success', 'promotional-tags');
    },
    loadedUserTags () {
      this.$bus.$emit('order-data-fetch-success', 'user-tags');
    },
    shipper_price_before_discount (newValue) {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (this.free_freight) {
        this.shipper_price = '0.00';
        this.shipper_additional_price = '0.00';
        this.shipper_additional_price_before_discount = '0.00';
        return;
      }

      if (newValue && this.$refs['order-customer'].selectedCustomer) {
        let shippingDiscount = this.prepareDiscount(this.discount_shipping);

        if (!this.include_discounts) {
          shippingDiscount = 0;
        }

        this.shipper_additional_price = CustomDecimals.roundProperly(this.shipper_additional_price_before_discount - ((shippingDiscount / 100) * this.shipper_additional_price_before_discount));
      } else if (newValue) {
        this.shipper_additional_price = CustomDecimals.roundProperly(this.shipper_additional_price_before_discount);
      }
    },
    include_discounts () {
      Vue.nextTick(() => {
        this.$bus.$emit('order-update-shipment-cost', true);
      });
    },
    allDataLoaded (newValue) {
      if (newValue && this.isEditMode) {
        this.setUpOrder();
      }
    },
    shipper_cartons_number (newValue, oldValue) {
      // eslint-disable-next-line
      if (newValue === oldValue || !newValue || !oldValue || newValue === 0 || newValue === '0' || oldValue === 0 || oldValue === '0' || isNaN(parseInt(newValue, 10) || isNaN(parseInt(oldValue, 10)))) {
        return;
      }

      if (!this.shipper_price_before_discount) {
        return;
      }

      this.debouncedShippingPriceCheck();
    },
    free_freight (newValue) {
      if (newValue) {
        this.shipper_price_before_discount = '0.00';
        this.shipper_additional_price_before_discount = '0.00';
      } else {
        this.updateShipmentPrice();
      }
    },
    offer_for_each_product (newValue) {
      if (newValue) {
        Vue.set(this, 'inquiry_range_services', false);
        Vue.set(this, 'join_products_in_prices', false);
      }
    },
    attach_service_prices (newValue) {
      if (newValue) {
        Vue.set(this, 'offer_for_each_product', true);
        Vue.set(this, 'inquiry_range_services', false);
        Vue.set(this, 'join_products_in_prices', false);
      }
    },
    is_advance (newValue, oldValue) {
      if (!this.orderSetUpInProgress && newValue !== oldValue && !!newValue) {
        this.advance_percent = 100;
      }
    },
    is_special_requirement (newValue, oldValue) {
      if (this.isEditMode && !this.orderSetUpInProgress && !oldValue && newValue) {
        let customerData = this.$refs['order-customer'].selectedCustomer;

        if (customerData && customerData.auto_special_requirement) {
          this.special_requirement = customerData.special_requirement;

          Vue.swal({
            title: this.$t('order.specialRequirementSetTitle'),
            html: this.$t('order.specialRequirementSetText'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }
    }
  },
  data() {
    return {
      attach_service_prices: false,
      offer_for_each_product: false,
      attached_services: {},
      attached_services_filter: '',
      attachment: null,
      block_erp_invoice_request: false,
      custom_shipping_price: false,
      delivery_note_file: null,
      connected_orders: [],
      inquiry_range_services: false,
      services_range_from: '',
      services_range_to: '',
      description: '',
      description_for_warehouse: '',
      internal_description: '',
      status: '',
      order_name: '',
      order_name_notes: '',
      tracking_number: '',
      is_priority: false,
      is_advance: false,
      is_cod: false,
      is_virtual: false,
      indent_offer_services_description: '',
      advance_paid: false,
      free_freight: false,
      join_products_in_prices: true,
      confirm_order: true,
      advance_percent: 100,
      contractor_id: null,
      deadline: null,
      stash_to: null,
      blockade_status: null,
      blocked_at: null,
      blockade_returned_by: null,
      blockade_returned_at: null,
      realization_date: null,
      discount_products: 0,
      discount_services: 0,
      discount_shipping: 0,
      dates_not_required: false,
      include_discounts: true,
      include_setup_prices: true,
      shipper_additional_price: '',
      shipper_additional_price_before_discount: '',
      shipper_price: '',
      shipper_price_before_discount: '',
      shipper_cartons_number: 1,
      processing_time_id: null,
      processing_time_name: '',
      transaction_type: null,
      custom_transaction_type: false,
      contractors: [],
      settlements: [],
      statuses: [],
      products: [],
      indentProducts: [],
      services: [],
      embroideryItems: [],
      currencies: [],
      servicesGroups: [],
      tagCategories: [],
      shipper_date: null,
      downloadingPdf: false,
      downloadingXls: false,
      downloadingSummary: false,
      emails: [],
      orderTags: [],
      promotionalTags: [],
      promotional_tag_id: null,
      userData: null,
      blockInterval: false,
      tags: {},
      is_repeated: false,
      repeat_number: '',
      is_box_marking: false,
      box_marking_number: '',
      is_special_requirement: false,
      special_requirement: '',
      warehouse: null,
      previousShippingPrice: null,
      previousCurrency: null,
      skonto: 0,
      confirmations: [],
      exclude_from_erp_integration: false,
      containers: [],
      container_id: null,
      orderDataInvoice: null,
      // indent
      indent_registry: null,
      customer_design_name: '',
      customer_order_name: '',
      customer_order_date: null,
      freight_type: null,
      original_shipment_date: null,
      // data dynamically loaded
      countries: [],
      // temporary data
      preferredCartonsAmount: 0,
      notAllProductsHaveCartonsAmount: false,
      selectedCustomer: null,
      selectedAddressData: null,
      selectedContainerProduct: null,
      selectedContainerOrderProduct: null,
      selectedPurchaseID: null,
      selectedPurchaseData: null,
      selectedProductInPurchase: null,
      selectedPurchaseProduct: null,
      selectedPurchaseOrderProduct: null,
      selectedPurchaseAdditionalData: null,
      selectedPurchaseProductCustomerID: null,
      selectedPurchaseProductSubCustomerID: null,
      selectedPersonData: null,
      usedContainers: [],
      foundedOrderNames: [],
      lastCheckedOrderName: '',
      invoiceDiffData: false,
      shipmentAddressData: {},
      shipmentContactData: {},
      increaseBoxesTimerInterval: false,
      decreaseBoxesTimerInterval: false,
      customerShipmentData: null,
      customerContactData: null,
      loadedConfirmations: [],
      nextCallbackForConfirmations: false,
      originalTrackingNumber: '',
      orderInvoiceDataUpdated: false,
      receivedGusData: null,
      // ui flags
      showContactPersonAddPopup: false,
      showAddressAddPopup: false,
      showAddressEditPopup: false,
      showAddressViewPopup: false,
      showComplaintNewPopup: false,
      showContainerSearchPopup: false,
      containerSearchPopupOfferMode: false,
      showGusComparisionPopup: false,
      showPurchaseSearchPopup: false,
      showPurchaseAddPopup: false,
      showPurchaseEditPopup: false,
      showConfirmationsPopup: false,
      showContactPersonViewPopup: false,
      showCustomerDiscountsPopup: false,
      missingProductAmount: 0,
      configureBlockade: false,
      showOrderContactPopup: false,
      showOrderContactEditPopup: false,
      selectedContactID: null,
      newAddressDefaultEmail: '',
      orderNameCheckInProgress: false,
      orderNameInUse: false,
      displayOrderNamePopup: false,
      skippedFirstOrderNameCheck: false,
      updateInvoiceDataInProgress: false,
      displayInvoiceDiffPopup: false,
      displayAddIndentProductPopup: false,
      selectedIndentProductIndexToAdd: false,
      blockExportTransactionType: false,
      blockDomesticTransactionType: false,
      blockNoEuVatTransactionType: false,
      taxNumberCheckInProgress: false,
      confirmationsCheckInProgress: false,
      processingTimeChangeToSave: false,
      followupInProgress: false,
      followUpSent: false,
      displayNewNotificationPopup: false,
      showEmbroideryAlert: false,
      showBoxesAlert: false,
      shippingPriceUpdateInProgress: false,
      errorDuringLoadingShippingPrice: false,
      showErpIntegrationPopup: false,
      excludeFromErpInProgress: false,
      includeToErpInProgress: false,
      customerChangedWithoutSave: false,
      // flags
      settlementInvoiceInProgress: false,
      loadError: false,
      loadedData: [],
      loadedCountries: false,
      loadedContractors: false,
      loadedCurrencies: false,
      loadedEmbroideryItems: false,
      loadedOrderTags: false,
      loadedPromotionalTags: false,
      loadedUserTags: false,
      loadedIndentProducts: false,
      loadedProducts: false,
      loadedServices: false,
      loadedServicesGroups: false,
      loadedTagCategories: false,
      loadedContainers: false,
      orderSetUpInProgress: false,
      dismissInProgress: false,
      showCollectAlert: false,
      downloadingAttachment: false,
      downloadingDeliveryNote: false,
      downloadingProforma: false,
      downloadingStock: false,
      invoiceCancelRequestSent: false,
      invoiceRequestSent: false,
      invoiceResetInProgress: false,
      addressHasBeenChanged: false,
      trackingNumberIsLocked: false,
      orderPricesUpdated: false,
      hasBeenSendAgainToEmbroidery: false,
      sendingAgainToEmbroideryInProgress: false,
      // internals
      spinnerTemplate: '<div class="spinner-border spinner-border-sm" role="status"></div>',
      immediateInvoiceRequest: false,
      containerName: false,
      originalContainerID: null,
      lockInProgress: false,
      // price change popup
      displayPriceChangesPopup: false,
      changedProductPrices: {},
      changedProductServicePrices: {},
      changedGlobalServicePrices: {}
    };
  },
  mounted () {
    if (this.isEditMode) {
      // refresh block order every 3 minutes
      this.blockOrder();
      this.blockInterval = setInterval(this.blockOrder, 3 * 60 * 1000);

      Vue.nextTick(() => {
        this.setLastActiveTab();
      });
      this.orderSetUpInProgress = true;
      this.showCollectAlert = this.orderData && !!this.orderData.collect_alert;
      this.$refs['order-customer'].loadCustomer(this.orderData.customer_id, this.orderData.address_id, this.orderData.contact_id);
      this.verifyTransactionType();
      this.shipmentAddressData = {
        receiver_name: this.orderData.address_company_name,
        receiver_address_country_code: this.orderData.address_country_id,
        receiver_address_postal_code: this.orderData.address_postcode,
        receiver_address_city: this.orderData.address_city,
        receiver_address_street: this.orderData.address_street,
        receiver_address_building: this.orderData.address_building,
        receiver_address_locale: this.orderData.address_locale,
        receiver_address_line_2: this.orderData.address_line_2,
        receiver_address_line_3: this.orderData.address_line_3
      };

      this.shipmentContactData = {
        receiver_attention_name: this.orderData.address_name,
        receiver_phone: this.orderData.address_phone,
        receiver_email: this.orderData.address_email
      };

      this.customerShipmentData = {
        company_name: this.orderData.invoice_name,
        attention_name: this.orderData.invoice_name,
        email: this.orderData.customer ? this.orderData.customer.email : '',
        phone: this.orderData.customer ? this.orderData.customer.phone : '',
        address_country_code: this.orderData.invoice_country_prefix,
        address_postal_code: this.orderData.invoice_postcode,
        address_city: this.orderData.invoice_city,
        address_street: this.orderData.invoice_street,
        address_building: this.orderData.invoice_building,
        address_locale: this.orderData.invoice_locale
      };

      this.customerContactData = {
        email: this.orderData.contact_email,
        name: this.orderData.contact_name,
        phone: this.orderData.contact_phone
      };

      if (this.orderData.invoice_country_prefix !== 'PL') {
        if (this.orderData.contact_name) {
          this.customerShipmentData.attention_name = this.orderData.contact_name;
        } else {
          this.customerShipmentData.attention_name = '';
        }
      }

      this.indent_offer_services_description = this.orderData.indent_offer_services_description;

      if (this.isIndentList && !this.isOffer) {
        this.originalContainerID = this.orderData.container_id;
        this.container_id = this.orderData.container_id;

        if (this.orderData.indent_container) {
          this.containerName = this.orderData.indent_container.number + ' (' + this.orderData.indent_container.date + ')';
        }
      }
    }

    if (!this.isEditMode && this.isIndentList) {
      this.free_freight = true;
    }

    this.loadAdditionalData();
    this.$bus.$on('order-update-shipment-cost', this.updateShipmentCost);
    this.$bus.$on('order-data-fetch-error', this.dataFetchError);
    this.$bus.$on('order-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$on('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$on('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$on('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$on('order-show-add-address-popup', this.showAddressNewPopup);
    this.$bus.$on('customer-view-address-popup-close', this.closeAddressViewPopup);
    this.$bus.$on('order-show-contact-person-view-popup', this.openContactPersonViewPopup);
    this.$bus.$on('customer-view-contact-person-popup-close', this.closeContactPersonViewPopup);
    this.$bus.$on('order-show-address-view-popup', this.openAddressViewPopup);
    this.$bus.$on('order-show-address-edit-popup', this.openAddressEditPopup);
    this.$bus.$on('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$on('container-search-popup-close', this.closeContainerSearchPopup);
    this.$bus.$on('container-search-popup-open', this.openContainerSearchPopup);
    this.$bus.$on('purchase-search-popup-close', this.closePurchaseSearchPopup);
    this.$bus.$on('purchase-search-popup-open', this.openPurchaseSearchPopup);
    this.$bus.$on('purchase-add-popup-close', this.closePurchaseAddPopup);
    this.$bus.$on('purchase-add-popup-open', this.openPurchaseAddPopup);
    this.$bus.$on('purchase-edit-popup-close', this.closePurchaseEditPopup);
    this.$bus.$on('purchase-edit-popup-open', this.openPurchaseEditPopup);
    this.$bus.$on('show-customer-discounts-popup', this.openCustomerDiscountsPopup);
    this.$bus.$on('close-customer-discounts-popup', this.closeCustomerDiscountsPopup);
    this.$bus.$on('order-select-customer-reload-customers-list', this.loadCustomerAfterDiscountsChange);
    this.$bus.$on('order-updated-products-qty', this.updateCartonsAmount);
    this.$bus.$on('order-updated-products-ids', this.updateCartonsAmount);
    this.$bus.$on('order-customer-id-changed', this.updateCustomerRelatedItems);
    this.$bus.$on('connected-orders-add', this.addConnectedOrder);
    this.$bus.$on('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$on('close-order-contact-edit-popup', this.closeOrderContactEditPopup);
    this.$bus.$on('close-order-name-popup', this.closeOrderNamePopup);
    this.$bus.$on('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$on('show-order-contact-edit-popup', this.openOrderContactEditPopup);
    this.$bus.$on('show-price-changes-popup', this.showPriceChangePopup);
    this.$bus.$on('close-price-changes-popup', this.hidePriceChangePopup);
    this.$bus.$on('close-invoice-diff-popup', this.hideInvoiceDiffPopup);
    this.$bus.$on('force-invoice-settlement-request', this.forceInvoiceRequestWithoutInvoiceDataCheck);
    this.$bus.$on('order-block-transaction-type-field', this.setBlockTransationTypeFieldStatus);
    this.$bus.$on('show-complaint-add-popup', this.openComplaintAddPopup);
    this.$bus.$on('close-complaint-add-popup', this.closeComplaintAddPopup);
    this.$bus.$on('order-update-shipment-address-id', this.orderShipmentAddressChanged);
    this.$bus.$on('order-new-shipment-added', this.newShipmentAdded);
    this.$bus.$on('order-form-save-before-leave', this.saveOrderBeforeLeave);
    this.$bus.$on('show-add-indent-product-popup', this.openAddIndentProductPopup);
    this.$bus.$on('close-add-indent-product-popup', this.closeAddIndentProductPopup);
    this.$bus.$on('order-change-indent-product-from-popup', this.reloadProductsAfterIndentProductAdd);
    this.$bus.$on('show-order-notification-new-popup', this.openNewNotificationPopup);
    this.$bus.$on('close-order-notification-new-popup', this.closeNewNotificationPopup);
    this.$bus.$on('show-order-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$on('close-order-erp-integration-popup', this.closeErpIntegrationPopup);
    this.$bus.$on('show-order-embroidery-alert', this.displayEmbroideryAlert);
    this.$bus.$on('show-order-boxes-alert', this.displayBoxesAlert);
    this.$bus.$on('order-check-shipping-price', this.debouncedShippingPriceCheck);
    this.$bus.$on('order-send-request-shipment-data', this.sendShipmentData);
    this.$bus.$on('order-disable-customer-discounts', this.disableCustomerDiscounts);
    this.$bus.$on('order-enable-customer-discounts', this.enableCustomerDiscounts);
    this.$bus.$on('ui-select-customer-auto-special-requirement', this.toggleAutoSpecialRequirement);
    this.$bus.$on('ui-select-customer-set-special-requirement', this.setSpecialRequirement);
    this.$bus.$on('close-order-confirmations-popup', this.closeConfirmationsPopup);
    this.$bus.$on('order-set-order-confirmations', this.handleConfirmations);
    this.$bus.$on('show-gus-comparision-popup', this.openGusComparisionPopup);
    this.$bus.$on('close-gus-comparision-popup', this.closeGusComparisionPopup);
    this.$bus.$on('order-check-customer-tax-number-before-invoice', this.checkCustomerTaxNumberBeforeInvoice);
    this.$bus.$on('order-customer-has-been-reset', this.orderCustomerHasBeenReset);
  },
  methods: {
    validateForm (nextCallback = false) {
      if (this.confirmationsCheckInProgress || this.taxNumberCheckInProgress || this.isLoading || this.orderSetUpInProgress) {
        if (typeof nextCallback === 'function') {
          nextCallback();
        }

        return;
      }

      if (parseFloat(this.shipper_price) < 0.01 && !this.free_freight) {
        Vue.swal({
          title: this.$t('order.invalidShippingCostTitle'),
          html: this.$t('order.invalidShippingCostDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.confirmationsCheckInProgress = true;

      let customerID = this.$refs['order-customer'].customer_id;
      let products = this.$refs['order-creator'].prepareProductsData();
      products = products.map(product => product.product_id);
      let paramsToSend = {
        return: 'get',
        where: {
          customer_id: customerID,
          product_id: products
        }
      };

      FormUtils.submitInBackground({
        $http: this.$http,
        formData: paramsToSend,
        endpoint: 'confirmations/items',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (data) => {
          this.confirmationsCheckInProgress = false;
          this.confirmations = [];
          this.nextCallbackForConfirmations = false;

          if (data.data.length) {
            this.nextCallbackForConfirmations = nextCallback;
            this.loadedConfirmations = data.data;
            this.showConfirmationsPopup = true;
          } else if (!data.data.length) {
            this.submitConfirmedForm(nextCallback);
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain') + ' (code: 1001)',
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.confirmationsCheckInProgress = false;
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.actionFailedTryAgain') + ' (code: 1002)',
            type: 'error',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    submitConfirmedForm (nextCallback) {
      if (this.isEditMode) {
        FormUtils.validate(this.$refs['order-form'], this.submitForm, nextCallback);
      } else {
        FormUtils.validate(this.$refs['order-form'], this.checkCustomerTaxNumberBeforeSave, nextCallback);
      }
    },
    checkCustomerTaxNumberBeforeSave (nextCallback = false) {
      let countryPrefix = this.$refs['order-customer'].selectedCustomer.invoice_country_prefix;
      let customerCountryID = this.$refs['order-customer'].selectedCustomer.country_id;
      let taxNumber = this.$refs['order-customer'].selectedCustomer.invoice_tax_number;

      if (this.transaction_type === 'NO_EU_VAT') {
        this.submitForm(nextCallback);
      } else {
        this.verifyCustomerTaxID(countryPrefix, taxNumber, customerCountryID, this.countries, this.submitForm, nextCallback);
      }
    },
    checkCustomerInvoiceDataBeforeInvoice (immediate) {
      if (!this.orderInvoiceDataUpdated) {
        Vue.swal({
          html:
            '<h3>' + this.$t('order.customerCheck.title') + '</h3>' +
            '<p>' + this.$t('order.customerCheck.desc') + '</p>' +
            '<button id="customer-check-before-1" class="swal2-confirm swal2-ok swal2-styled">' + this.$t('order.customerCheck.ok') + '</button>' +
            '<button id="customer-check-before-2" class="swal2-confirm swal2-check swal2-styled">' + this.$t('order.customerCheck.check') + '</button>' +
            '<button id="customer-check-before-3" class="swal2-confirm swal2-danger swal2-styled">' + this.$t('ui.cancel') + '</button>',
          showConfirmButton: false,
          showCancelButton: false,
          onBeforeOpen: () => {
            let modal = Vue.swal.getPopup();
            modal.querySelector('#customer-check-before-1').addEventListener('click', () => Vue.swal.close('ok'));
            modal.querySelector('#customer-check-before-2').addEventListener('click', () => Vue.swal.close('check'));
            modal.querySelector('#customer-check-before-3').addEventListener('click', () => Vue.swal.close('cancel'));
          }
        }).then((result) => {
          if (result === 'ok') {
            this.checkCustomerTaxNumberBeforeInvoice(immediate);
          } else if (result === 'check') {
            this.$bus.$emit('order-select-customer-verify-tax-id', immediate);
          }
        });
      } else {
        this.checkCustomerTaxNumberBeforeInvoice(immediate);
      }
    },
    checkCustomerTaxNumberBeforeInvoice (immediate) {
      let countryPrefix = this.orderData.invoice_country_prefix;
      let customerCountryID = this.orderData.customer ? this.orderData.customer.country_id : 0;
      let taxNumber = this.orderData.invoice_tax_number;
      this.immediateInvoiceRequest = immediate;

      if (this.transaction_type === 'NO_EU_VAT') {
        this.requestSettlementInvoice(false);
      } else {
        this.verifyCustomerTaxID(countryPrefix, taxNumber, customerCountryID, this.countries, this.requestSettlementInvoice, false);
      }
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/contractors/items',
        loadedKey: 'loadedContractors',
        outputKey: 'contractors',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      if (this.isIndentList && !this.isOffer) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/containers/items?return=get&where[status]=OPEN',
          loadedKey: 'loadedContainers',
          outputKey: 'containers',
          errorKey: 'loadError'
        });
      }

      if (this.isIndentList && this.isOffer) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: 0,
              order_id: this.isEditMode ? parseInt(this.$route.params.id, 10) : 0
            }
          }
        });

        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedIndentProducts',
          outputKey: 'indentProducts',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: 1,
              order_id: this.isEditMode ? parseInt(this.$route.params.id, 10) : 0
            }
          }
        });
      } else {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: +this.isIndentList,
              order_id: this.isEditMode ? parseInt(this.$route.params.id, 10) : 0
            }
          }
        });
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError',
        params: {
          where: {
            is_indent: +this.isIndentList,
            order_id: this.isEditMode ? parseInt(this.$route.params.id, 10) : 0
          }
        },
        successAction: () => {
          if (!this.isEditMode) {
            this.attached_services = this.setUpAttachedServicePrices([]);
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/services/groups/all-items'),
        loadedKey: 'loadedServicesGroups',
        outputKey: 'servicesGroups',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/tags/categories/items'),
        loadedKey: 'loadedTagCategories',
        outputKey: 'tagCategories',
        errorKey: 'loadError',
        params: {
          with: ['tags']
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/orders/tags/items',
        loadedKey: 'loadedOrderTags',
        outputKey: 'orderTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/orders/promotional-tags/items',
        loadedKey: 'loadedPromotionalTags',
        outputKey: 'promotionalTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/embroidery/items',
        loadedKey: 'loadedEmbroideryItems',
        outputKey: 'embroideryItems',
        errorKey: 'loadError'
      });

      if (!this.isEditMode) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/users/item/' + parseInt(this.$store.getters.getUserID, 10),
          loadedKey: 'loadedUserTags',
          outputKey: 'userData',
          errorKey: 'loadError',
          params: {
            with: ['orderTags']
          },
          successAction: () => {
            if (this.userData && this.userData.order_tags) {
              this.setUpTags(this.userData.order_tags);
            }
          }
        });
      }
    },
    reloadProductsAfterIndentProductAdd (productIndex, productID) {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError',
        params: {
          where: {
            is_indent: 1,
            order_id: this.isEditMode ? parseInt(this.$route.params.id, 10) : 0
          },
          reset_cache: 1
        },
        successAction: () => {
          Vue.nextTick(() => {
            // we need to update product prices data
            this.$refs['order-creator'].setUpProductsPricesData();
            // the below line will set product and update prices
            this.$bus.$emit('products-search-popup-select-product', productID, productIndex, 'skip');
          });
        }
      });
    },
    submitForm (nextCallback = false) {
      let dataToSend = this.prepareFormData();
      let filesToSend = this.prepareFormFiles();
      this.$bus.$emit('order-form-submit-in-progress', true);

      if (this.isEditMode) {
        FormUtils.submit({
          $http: this.$http,
          endpoint: '/orders/update/' + parseInt(this.$route.params.id, 10),
          formData: dataToSend,
          successTitle: ((this.isOrderMode || this.isOrder) ? this.$t('purchase.editOrderSuccessTitle') : this.$t('inquiries.editOfferSuccessTitleShort')),
          successText: ((this.isOrderMode || this.isOrder) ? this.$t('purchase.editOrderSuccessText') : this.$t('inquiries.editOfferSuccessText')),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: ((this.isOrderMode || this.isOrder) ? this.$t('purchase.editOrderErrorText') : this.$t('inquiries.editOfferErrorText')),
          successAction: () => {
            if (typeof nextCallback === 'function') {
              this.$bus.$emit('sidebar-counters-refresh');
              this.skipOrderReload = false;
              nextCallback();
            } else {
              this.$bus.$emit('order-wrapper-reload');
              this.$bus.$emit('sidebar-counters-refresh');
              this.$bus.$emit('order-form-submit-in-progress', false);
            }
          },
          failAction: () => {
            this.$bus.$emit('order-form-submit-in-progress', false);
          }
        }, filesToSend);
      } else {
        if (this.isOrderMode) {
          if (this.isIndentList) {
            dataToSend.save_with_status = 'IN_PRODUCTION';
          } else {
            dataToSend.save_with_status = 'TO_COLLECT';
          }
        }

        FormUtils.submit({
          $http: this.$http,
          endpoint: '/orders/store',
          formData: dataToSend,
          successTitle: (this.isOrderMode || this.isOrder) ? this.$t('purchase.addOrderSuccessTitle') : this.$t('inquiries.addOfferSccessTitle'),
          successText: ((this.isOrderMode || this.isOrder) ? this.$t('purchase.addOrderSuccessText') : this.$t('inquiries.addOfferSuccessText')),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: ((this.isOrderMode || this.isOrder) ? this.$t('purchase.addOrderErrorText') : this.$t('inquiries.addOfferErrorText')),
          successAction: (response) => {
            this.$bus.$emit('sidebar-counters-refresh');

            if (typeof nextCallback === 'function') {
              nextCallback();
              return;
            }

            if (this.$refs['order-tabs'].currentTab === 0) {
              window.localStorage.setItem('headwear-crm-last-active-tab', 'order-data');
            } else if (this.$refs['order-tabs'].currentTab === 1) {
              window.localStorage.setItem('headwear-crm-last-active-tab', 'order-content');
            }

            if (this.isOrderMode) {
              if (this.isIndentList) {
                this.$router.push('/zamowienia-indent/edycja/' + parseInt(response.id, 10));
              } else {
                this.$router.push('/zamowienia/edycja/' + parseInt(response.id, 10));
              }
            } else if (this.isIndentList) {
              this.$router.push('/oferty-indent/edycja/' + parseInt(response.id, 10));
            } else {
              this.$router.push('/oferty/edycja/' + parseInt(response.id, 10));
            }

            this.$bus.$emit('order-form-submit-in-progress', false);
          },
          failAction: () => {
            this.$bus.$emit('order-form-submit-in-progress', false);
          }
        }, filesToSend);
      }
    },
    prepareFormData () {
      let formData = {
        attach_service_prices: this.attach_service_prices,
        offer_for_each_product: this.offer_for_each_product,
        attached_services: this.prepareAttachedServicePrices(this.attached_services),
        block_erp_invoice_request: this.is_advance ? +this.block_erp_invoice_request : 0,
        customer_id: this.$refs['order-customer'].customer_id,
        contact_id: this.$refs['order-contact'].contact_id,
        connected: this.connected_orders.map(order => order.id),
        brand: this.$refs['order-brand'].brand,
        address_id: this.$refs['order-address'].address_id,
        currency_id: this.$refs['order-currency'].currency_id,
        language_id: this.$refs['order-lang'].language_id,
        description_for_warehouse: this.description_for_warehouse,
        internal_description: this.internal_description,
        products: this.$refs['order-creator'].prepareProductsData(),
        services: this.$refs['order-creator'].prepareServicesData(),
        order_name: (this.order_name).toString().toLocaleUpperCase(),
        order_name_notes: (this.order_name_notes).toString().toLocaleUpperCase(),
        is_indent: this.isIndentList,
        is_priority: this.is_priority,
        is_advance: this.is_advance,
        is_cod: this.is_cod,
        is_virtual: this.is_virtual,
        advance_paid: this.advance_paid,
        advance_percent: this.is_advance ? this.advance_percent : 0,
        confirm_order: this.confirm_order,
        contractor_id: this.contractor_id,
        include_discounts: this.include_discounts,
        include_setup_prices: this.include_setup_prices,
        dates_not_required: this.dates_not_required,
        deadline: this.deadline ? [this.deadline.getFullYear(), this.deadline.getMonth() + 1, this.deadline.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        stash_to: this.stash_to ? [this.stash_to.getFullYear(), this.stash_to.getMonth() + 1, this.stash_to.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        shipper_date: this.shipper_date ? [this.shipper_date.getFullYear(), this.shipper_date.getMonth() + 1, this.shipper_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        free_freight: this.free_freight,
        join_products_in_prices: this.join_products_in_prices,
        inquiry_range_services: this.inquiry_range_services,
        services_range_from: this.services_range_from,
        services_range_to: this.services_range_to,
        shipper_id: this.$refs['order-shipper'].shipper_id,
        transaction_type: this.transaction_type,
        custom_transaction_type: +this.custom_transaction_type,
        shipper_additional_price: Number(this.shipper_additional_price).toFixed(2),
        shipper_additional_price_before_discount: Number(this.shipper_additional_price_before_discount).toFixed(2),
        shipper_price_before_discount: Number(this.shipper_price_before_discount).toFixed(2),
        shipper_cartons_number: parseInt(this.shipper_cartons_number, 10),
        shipper_price: Number(this.shipper_price).toFixed(2),
        discount_products: Number(this.prepareDiscount(this.discount_products)).toFixed(2),
        discount_services: Number(this.prepareDiscount(this.discount_services)).toFixed(2),
        discount_shipping: Number(this.prepareDiscount(this.discount_shipping)).toFixed(2),
        blockade_status: this.blockade_status,
        tags: this.prepareTags(),
        promotional_tag_id: this.promotional_tag_id,
        is_repeated: this.is_repeated,
        repeat_number: this.repeat_number,
        is_box_marking: this.is_box_marking,
        box_marking_number: this.box_marking_number,
        is_special_requirement: this.is_special_requirement,
        special_requirement: this.special_requirement,
        warehouse: this.warehouse,
        skonto: Number(this.skonto).toFixed(2),
        custom_shipping_price: +this.custom_shipping_price,
        confirmations: this.confirmations,
        services_packages: this.$refs['order-creator'].prepareServicesPackagesData()
      };

      if (this.isIndentList) {
        formData.indent_offer_services_description = this.indent_offer_services_description;

        if (!this.isOffer) {
          if (!this.isEditMode && this.container_id) {
            formData.container_id = this.container_id;
          } else if (this.isEditMode && this.container_id && this.originalContainerID !== this.container_id) {
            formData.container_id = this.container_id;
          } else if (this.originalContainerID && !this.container_id) {
            formData.container_id = 0;
          }
        }
      }

      if (this.isIndentList && this.isEditMode) {
        formData.indent_registry = this.indent_registry;
        formData.customer_order_name = this.customer_order_name;
        formData.customer_design_name = this.customer_design_name;
        formData.customer_order_date = this.customer_order_date ? [this.customer_order_date.getFullYear(), this.customer_order_date.getMonth() + 1, this.customer_order_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null;
        formData.freight_type = this.freight_type;
        formData.original_shipment_date = this.original_shipment_date ? [this.original_shipment_date.getFullYear(), this.original_shipment_date.getMonth() + 1, this.original_shipment_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null;
      }

      if (!this.trackingNumberIsLocked) {
        formData.tracking_number = this.tracking_number;
        formData.description = this.description;
      }

      if (!this.isEditMode || this.processingTimeChangeToSave) {
        formData.processing_time_id = this.$refs['order-time'].processing_time_id;
      } else {
        formData.processing_time_id = this.processing_time_id;
      }

      if (this.isEditMode) {
        if (this.addressHasBeenChanged) {
          formData.address_changed = true;
        } else {
          formData.address_changed = false;
        }
      }

      if (this.attachment === '') {
        formData.attachment = '';
      }

      if (this.delivery_note_file === '') {
        formData.delivery_note_file = '';
      }

      if (this.$route.params.type === 'zamowienie') {
        if (this.isIndentList) {
          formData.save_with_status = 'IN_PRODUCTION';
        } else {
          formData.save_with_status = 'TO_COLLECT';
        }
      }

      if (this.orderPricesUpdated) {
        formData.order_prices_updated = true;
      }

      if (this.isEditMode && this.deadline === null) {
        formData.deadline = 0;
      }

      if (this.isEditMode && this.shipper_date === null) {
        formData.shipper_date = 0;
      }

      if (this.isEditMode && this.isIndentList && this.customer_order_date === null) {
        formData.customer_order_date = 0;
      }

      if (this.isEditMode && this.isIndentList && this.original_shipment_date === null) {
        formData.original_shipment_date = 0;
      }

      return formData;
    },
    prepareFormFiles () {
      let files = [];

      for (let i = 0; i < this.$refs['order-creator'].productsData.length; i++) {
        let productData = this.$refs['order-creator'].productsData[i];

        if (productData.fileToUpload) {
          files.push({
            key: 'products[' + i + '][file]',
            value: productData.fileToUpload
          });
        }
      }

      if (this.attachment) {
        files.push({
          key: 'attachment',
          value: this.attachment
        });
      }

      if (this.delivery_note_file) {
        files.push({
          key: 'delivery_note_file',
          value: this.delivery_note_file
        });
      }

      return files;
    },
    showContactPersonNewPopup () {
      this.showContactPersonAddPopup = true;
    },
    closeContactPersonAddPopup (contactID) {
      this.showContactPersonAddPopup = false;

      if (contactID) {
        this.$bus.$emit('order-select-contact-fetch-data', contactID);
      }
    },
    showAddressNewPopup () {
      let selectedContact = this.$refs['order-contact'] ? this.$refs['order-contact'].getContact() : null;
      this.newAddressDefaultEmail = selectedContact && selectedContact.email ? selectedContact.email : '';
      this.showAddressAddPopup = true;
    },
    closeAddressAddPopup (addressID) {
      this.showAddressAddPopup = false;

      if (addressID) {
        this.$bus.$emit('order-select-address-fetch-data', addressID);
      }
    },
    updateShipmentPrice () {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (this.free_freight) {
        this.shipper_price = '0.00';
        this.shipper_additional_price = '0.00';
        this.shipper_price_before_discount = '0.00';
        this.shipper_additional_price_before_discount = '0.00';
        return;
      }

      if (
        this.$refs['order-customer'].selectedCustomer &&
        (
          (!this.isEditMode && this.$refs['order-address'].selectedAddress) ||
          (this.isEditMode && this.addressHasBeenChanged && this.$refs['order-address'].selectedAddress) ||
          (this.isEditMode && !this.addressHasBeenChanged && this.orderData.address_country_id)
        ) &&
        this.$refs['order-shipper'].selectedShipper
      ) {
        this.debouncedShippingPriceCheck();
      } else {
        this.shipper_price_before_discount = Number(this.shipper_price).toFixed(2);
      }
    },
    updateShipmentPriceWithDiscount () {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (this.free_freight) {
        this.shipper_price = '0.00';
        this.shipper_additional_price = '0.00';
        this.shipper_price_before_discount = '0.00';
        this.shipper_additional_price_before_discount = '0.00';
        return;
      }

      if (
        this.$refs['order-customer'] && this.$refs['order-customer'].selectedCustomer &&
        // We are not using country ID in the calculations so we can just check if the country in address is set - it means that address is set
        (
          (!this.isEditMode && this.$refs['order-address'] && this.$refs['order-address'].selectedAddress) ||
          (this.isEditMode && this.addressHasBeenChanged && this.$refs['order-address'] && this.$refs['order-address'].selectedAddress) ||
          (this.isEditMode && !this.addressHasBeenChanged && this.orderData.address_country)
        ) &&
        this.$refs['order-shipper'] && this.$refs['order-shipper'].selectedShipper &&
        this.shipper_price
      ) {
        this.debouncedShippingPriceCheck();
      } else {
        this.shipper_price = Number(this.shipper_price_before_discount).toFixed(2);
        this.shipper_additional_price = Number(this.shipper_additional_price_before_discount).toFixed(2);
      }
    },
    dataFetchError () {
      this.loadError = true;
    },
    dataFetchSuccess (dataKey) {
      if (this.loadedData.indexOf(dataKey) === -1) {
        this.loadedData.push(dataKey);
        this.loadedData.sort();
      }
    },
    updateShipmentCost (withDiscount = false) {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (withDiscount) {
        this.updateShipmentPriceWithDiscount();
      } else {
        this.updateShipmentPrice();
      }
    },
    updateCustomerRelatedItems () {
      if (this.orderSetUpInProgress) {
        return;
      }

      let customerData = this.$refs['order-customer'].selectedCustomer;
      this.orderInvoiceDataUpdated = false;

      if (this.orderData && customerData) {
        if (this.orderData.customer_id === customerData.id) {
          this.customerChangedWithoutSave = false;
        } else {
          this.customerChangedWithoutSave = true;
        }
      }

      if (!customerData) {
        return;
      }

      let languageToSet = customerData.language_id;
      let currencyToSet = customerData.currency_id;
      let shipperToSet = customerData.shipper_id;
      let paymentDays = customerData.payment_days;
      let ordersAdvancePaidCount = customerData.orders_advance_paid_count;

      if (languageToSet) {
        Vue.set(this.$refs['order-lang'], 'language_id', languageToSet);
      }

      if (currencyToSet) {
        Vue.set(this.$refs['order-currency'], 'currency_id', currencyToSet);
      }

      if (shipperToSet) {
        Vue.set(this.$refs['order-shipper'], 'shipper_id', shipperToSet);
      }

      if (paymentDays === 0) {
        Vue.set(this, 'is_advance', true);

        if (ordersAdvancePaidCount >= 3) {
          Vue.swal({
            title: this.$t('order.canChangeAdvanceToNormalPayment'),
            html: this.$t('order.canChangeAdvanceToNormalPaymentDesc'),
            type: 'info',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      } else {
        Vue.set(this, 'is_advance', false);
      }

      if (
        customerData &&
        customerData.invoice_country_prefix === 'PL' &&
        !this.custom_transaction_type
      ) {
        let previousTransacitonType = this.transaction_type;
        this.transaction_type = 'DOMESTIC';
        Vue.set(this, 'blockDomesticTransactionType', true);

        if (previousTransacitonType !== this.transaction_type) {
          Vue.swal({
            title: this.$t('order.transactionTypeSetToDomestic'),
            html: this.$t('order.transactionTypeSetToDomesticDesc'),
            type: 'info',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      } else {
        Vue.set(this, 'blockDomesticTransactionType', false);
      }

      this.setDiscountsToCustomerLevel();
    },
    setUpOrder () {
      this.attached_services = this.setUpAttachedServicePrices(this.orderData.attached_services);
      this.attach_service_prices = !!this.orderData.attach_service_prices;
      this.offer_for_each_product = !!this.orderData.offer_for_each_product;
      this.inquiry_range_services = !!this.orderData.inquiry_range_services;
      this.services_range_from = this.orderData.services_range_from;
      this.services_range_to = this.orderData.services_range_to;
      this.connected_orders = JSON.parse(JSON.stringify(this.orderData.connected));
      this.$refs['order-brand'].brand = this.orderData.brand;
      this.$refs['order-currency'].currency_id = this.orderData.currency_id;
      this.$refs['order-lang'].language_id = this.orderData.language_id;
      this.$refs['order-time'].processing_time_id = this.orderData.processing_time_id;
      this.processing_time_id = this.orderData.processing_time_id;
      this.processing_time_name = this.orderData.processing_time_name;
      this.$refs['order-creator'].setUpProducts(this.orderData.products);
      this.$refs['order-creator'].setUpServices(this.orderData.services, this.orderData.services_packages);
      this.custom_shipping_price = !!this.orderData.custom_shipping_price;

      if (this.$refs['request-creator']) {
        this.$refs['request-creator'].setUpProducts(this.orderData.offer_products);
        this.$refs['request-creator'].setUpServices(this.orderData.offer_services_groups);
      }

      this.status = this.orderData.status;
      this.statuses = this.orderData.statuses;
      this.settlements = this.orderData.settlements;
      this.include_discounts = !!this.orderData.include_discounts;
      this.discount_products = this.orderData.discount_products;
      this.discount_services = this.orderData.discount_services;
      this.discount_shipping = this.orderData.discount_shipping;
      this.include_setup_prices = !!this.orderData.include_setup_prices;
      this.dates_not_required = !!this.orderData.dates_not_required;
      this.shipper_cartons_number = parseInt(this.orderData.shipper_cartons_number, 10);
      Vue.set(this, 'description', this.orderData.description);
      Vue.set(this, 'description_for_warehouse', this.orderData.description_for_warehouse);
      Vue.set(this, 'internal_description', this.orderData.internal_description);
      this.order_name = this.orderData.order_name;
      this.order_name_notes = this.orderData.order_name_notes;
      this.tracking_number = this.orderData.tracking_number;
      this.originalTrackingNumber = this.orderData.tracking_number;
      this.block_erp_invoice_request = !!this.orderData.block_erp_invoice_request;
      this.is_advance = !!this.orderData.is_advance;
      this.is_cod = !!this.orderData.is_cod;
      this.is_virtual = !!this.orderData.is_virtual;
      this.advance_paid = !!this.orderData.advance_paid;
      this.confirm_order = !!this.orderData.confirm_order;
      this.advance_percent = this.orderData.advance_percent;
      this.contractor_id = this.orderData.contractor_id;
      this.deadline = this.orderData.deadline;
      this.stash_to = this.orderData.stash_to;
      this.realization_date = this.orderData.realization_date;
      this.blockade_status = this.orderData.blockade_status;
      this.blocked_at = this.orderData.blocked_at;
      this.blockade_returned_by = this.orderData.blockade_returned_by;
      this.blockade_returned_at = this.orderData.blockade_returned_at;
      this.shipper_date = this.orderData.shipper_date;
      this.attachment = this.orderData.attachment;
      this.delivery_note_file = this.orderData.delivery_note_file;
      this.is_priority = !!this.orderData.is_priority;
      this.free_freight = !!this.orderData.free_freight;
      this.join_products_in_prices = !!this.orderData.join_products_in_prices;
      this.emails = this.orderData.emails;
      this.transaction_type = this.orderData.transaction_type;
      this.custom_transaction_type = !!this.orderData.custom_transaction_type;
      this.setUpTags(this.orderData.tags);
      this.promotional_tag_id = this.orderData.promotional_tag_id;
      this.is_repeated = !!this.orderData.is_repeated;
      this.repeat_number = this.orderData.repeat_number;
      this.is_box_marking = !!this.orderData.is_box_marking;
      this.box_marking_number = this.orderData.box_marking_number;
      this.is_special_requirement = !!this.orderData.is_special_requirement;
      this.special_requirement = this.orderData.special_requirement;
      this.warehouse = this.orderData.warehouse;
      this.previousShippingPrice = this.orderData.shipper_price;
      this.previousCurrency = this.orderData.currency_id;
      this.skonto = this.orderData.skonto;
      this.exclude_from_erp_integration = this.orderData.exclude_from_erp_integration;
      this.orderDataInvoice = this.orderData.order_data_invoice;

      if (this.isIndentList && this.isEditMode) {
        this.indent_registry = this.orderData.indent_registry;
        this.customer_order_name = this.orderData.customer_order_name;
        this.customer_design_name = this.orderData.customer_design_name;
        this.customer_order_date = this.orderData.customer_order_date;
        this.freight_type = this.orderData.freight_type;
        this.original_shipment_date = this.orderData.original_shipment_date;
      }

      setTimeout(() => {
        if (this.$refs['order-shipper']) {
          this.$refs['order-shipper'].shipper_id = this.orderData.shipper_id;
        }

        setTimeout(() => {
          this.shipper_price = this.orderData.shipper_price;
          this.shipper_additional_price = this.orderData.shipper_additional_price;
          this.shipper_additional_price_before_discount = this.orderData.shipper_additional_price_before_discount;
          this.shipper_price_before_discount = Number(this.orderData.shipper_price_before_discount).toFixed(2);
          this.orderSetUpInProgress = false;
        }, 1000);
      }, 1000);

      Vue.nextTick(() => {
        this.setUpOrderItemsPricesData();
      });
    },
    setUpOrderItemsPricesData () {
      this.$refs['order-creator'].setUpProductsPricesData();
      this.$refs['order-creator'].setUpServicesPricesData();
    },
    downloadPdf () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingPdf = orderID;

      this.$http.post('/orders/offer/' + orderID, {
        type: 'pdf'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPdf = false;
      });
    },
    downloadXls () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingXls = orderID;

      this.$http.post('/orders/offer/' + orderID, {
        type: 'xlsx'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('order.orderFileNamePrefix');

        if (orderStatusPhase['offer-file-prefix'].indexOf(this.status) > -1) {
          filenamePrefix = this.$t('order.offerFileNamePrefix');
        }

        link.setAttribute('download', filenamePrefix + orderID + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingXls = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingXls = false;
      });
    },
    downloadProforma () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingProforma = orderID;

      this.$http.post('/orders/invoice-proforma/' + orderID, {
        type: 'pdf'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('order.orderProformaFileNamePrefix');
        link.setAttribute('download', filenamePrefix + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingProforma = false;
      });
    },
    downloadSummary () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingSummary = orderID;

      this.$http.post('/orders/summary/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingSummary = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingSummary = false;
      });
    },
    downloadStock () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingStock = orderID;

      this.$http.post('/orders/download-products-stock/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingStock = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingStock = false;
      });
    },
    hasDeadlineWarning () {
      if (this.orderData && this.orderData.warnings && this.orderData.warnings.filter(warning => warning.type === 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    dismissCollectAlert () {
      if (this.dismissInProgress) {
        return;
      }

      this.dismissInProgress = true;
      this.showCollectAlert = false;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/disable-collect-alert/' + this.orderData.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          this.dismissInProgress = false;
          this.showCollectAlert = false;
        },
        failAction: () => {
          this.dismissInProgress = false;
          this.showCollectAlert = true;
        }
      });
    },
    openAddressEditPopup () {
      this.selectedAddressData = this.$refs['order-address'].getAddress();
      this.showAddressEditPopup = true;
    },
    closeAddressEditPopup () {
      this.showAddressEditPopup = false;
    },
    openAddressViewPopup () {
      this.selectedAddressData = this.$refs['order-address'].getAddress();
      this.showAddressViewPopup = true;
    },
    closeAddressViewPopup () {
      this.showAddressViewPopup = false;
    },
    openContactPersonViewPopup () {
      this.selectedPersonData = this.$refs['order-contact'].getContact();
      this.showContactPersonViewPopup = true;
    },
    closeContactPersonViewPopup () {
      this.showContactPersonViewPopup = false;
    },
    openContainerSearchPopup (productID, orderProductID, missingAmount, containers, isOffer) {
      this.selectedContainerProduct = productID;
      this.selectedContainerOrderProduct = orderProductID;
      this.missingProductAmount = missingAmount;
      this.usedContainers = containers;
      this.showContainerSearchPopup = true;
      this.containerSearchPopupOfferMode = isOffer;
    },
    closeContainerSearchPopup () {
      this.showContainerSearchPopup = false;
    },
    openPurchaseSearchPopup (productID, orderProductID, missingAmount, purchaseID) {
      this.selectedPurchaseProduct = productID;
      this.selectedPurchaseOrderProduct = orderProductID;
      this.missingProductAmount = missingAmount;
      this.selectedPurchaseID = purchaseID;
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;
      this.showPurchaseSearchPopup = true;
    },
    closePurchaseSearchPopup () {
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;
      this.showPurchaseSearchPopup = false;
    },
    openPurchaseAddPopup (productID, orderProductID, missingAmount, purchaseID) {
      this.selectedPurchaseProduct = productID;
      this.selectedPurchaseOrderProduct = orderProductID;
      this.missingProductAmount = missingAmount;
      this.selectedPurchaseID = purchaseID;
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;

      if (productID && this.products) {
        let filteredProducts = this.products.filter(product => product.id === productID);

        if (filteredProducts && filteredProducts[0]) {
          this.selectedPurchaseProductCustomerID = filteredProducts[0].purchase_customer_id;
          this.selectedPurchaseProductSubCustomerID = filteredProducts[0].purchase_sub_customer_id;
        }
      }

      this.showPurchaseAddPopup = true;
    },
    closePurchaseAddPopup () {
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;
      this.showPurchaseAddPopup = false;
    },
    openPurchaseEditPopup (productID, purchaseProductID, orderProductID, additionalData, purchaseData) {
      this.selectedPurchaseProduct = productID;
      this.selectedProductInPurchase = purchaseProductID;
      this.selectedPurchaseOrderProduct = orderProductID;
      this.selectedPurchaseAdditionalData = additionalData;
      this.selectedPurchaseData = purchaseData;
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;
      this.showPurchaseEditPopup = true;
    },
    closePurchaseEditPopup () {
      this.selectedPurchaseProductCustomerID = null;
      this.selectedPurchaseProductSubCustomerID = null;
      this.showPurchaseEditPopup = false;
    },
    openOrderContactPopup () {
      this.showOrderContactPopup = true;
    },
    closeOrderContactPopup () {
      this.showOrderContactPopup = false;
    },
    openOrderContactEditPopup (contactID) {
      this.showOrderContactEditPopup = true;
      this.selectedContactID = contactID;
    },
    closeOrderContactEditPopup () {
      this.showOrderContactEditPopup = false;
      this.selectedContactID = null;
    },
    setAttachment (e) {
      this.attachment = e.target.files[0];
    },
    removeAttachment () {
      this.attachment = '';
    },
    downloadAttachment () {
      this.downloadingAttachment = true;

      this.$http.post('/orders/download-order-attachment/' + this.orderData.id, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingAttachment = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingAttachment = false;
      });
    },
    setDeliveryNote (e) {
      this.delivery_note_file = e.target.files[0];
    },
    removeDeliveryNote () {
      this.delivery_note_file = '';
    },
    downloadDeliveryNote () {
      this.downloadingDeliveryNote = true;

      this.$http.post('/orders/download-order-delivery-note/' + this.orderData.id, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingDeliveryNote = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingDeliveryNote = false;
      });
    },
    requestSettlementInvoice (skipInvoiceDataCheck = false) {
      let invoiceDataConsistency = this.compareInvoiceData();

      if (!skipInvoiceDataCheck && invoiceDataConsistency !== true) {
        this.invoiceDiffData = JSON.parse(JSON.stringify(invoiceDataConsistency));
        this.displayInvoiceDiffPopup = true;
        return;
      }

      if (!this.orderData.address_id) {
        Vue.swal({
          title: this.$t('order.addressIsRequiredForInvoice'),
          html: this.$t('order.addressIsRequiredForInvoiceDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      if (!this.orderData.invoice_country_prefix) {
        Vue.swal({
          title: this.$t('order.invoiceCountryPrefixIsRequiredForInvoice'),
          html: this.$t('order.invoiceCountryPrefixIsRequiredForInvoiceDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.settlementInvoiceInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        formData: {
          immediately: +this.immediateInvoiceRequest
        },
        endpoint: '/orders/request-invoice/' + parseInt(this.$route.params.id, 10),
        successTitle: this.$t('order.requestSent'),
        successText: this.$t('order.requestForBillingInvoiceSentSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.requestSentErrorText'),
        errorMessages: {
          'INVALID_STATUS': this.$t('order.errors.invalid_status'),
          'INVALID_ADDRESS_COUNTRY_PREFIX': this.$t('order.errors.invalid_address_country_prefix'),
          'INVALID_INVOICE_COUNTRY_PREFIX': this.$t('order.errors.invalid_invoice_country_prefix'),
          'EMPTY_PRICES': this.$t('order.errors.empty_prices'),
          'EMPTY_RECEIVER_EMAIL': this.$t('order.errors.empty_receiver_email'),
          'EMPTY_TRACKING_NUMBER': this.$t('order.errors.empty_tracking_number'),
          'INVOICE_REQUEST': this.$t('order.errors.invoice_request'),
          'INVOICE_EXISTS': this.$t('order.errors.invoice_exists'),
          'NOT_SYNCHRONIZED': this.$t('order.errors.not_synchronized'),
          'PURCHASE_NOT_CLOSED': this.$t('order.errors.purchase_not_closed'),
          'CONTAINER_NOT_CLOSED': this.$t('order.errors.container_not_closed'),
          'EXCLUDE_FROM_ERP_INTEGRATION': this.$t('order.errors.exclude_from_erp_integration'),
          'BLOCK_ERP_INVOICE_REQUEST': this.$t('order.errors.block_erp_invoice_request')
        },
        successAction: () => {
          this.settlementInvoiceInProgress = false;
          this.invoiceRequestSent = true;
        },
        failAction: () => {
          this.settlementInvoiceInProgress = false;
        }
      });
    },
    resetInvoiceRequest () {
      this.invoiceResetInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/reset-request-invoice/' + parseInt(this.$route.params.id, 10),
        successTitle: this.$t('order.invoiceRequestCancelTitle'),
        successText: this.$t('order.invoiceRequestCancelText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.invoiceRequestCancelError'),
        successAction: () => {
          this.invoiceResetInProgress = false;
          this.orderData.invoice_request = false;
          this.orderData.invoice_request_by_user = false;
          this.invoiceRequestSent = false;
          this.invoiceCancelRequestSent = true;
        },
        failAction: () => {
          this.invoiceResetInProgress = false;
        }
      });
    },
    getServiceNameByID (id) {
      let foundedService = this.services.filter(service => service.id === parseInt(id, 10));

      if (foundedService[0]) {
        return [foundedService[0].group_name, foundedService[0].name].filter(item => !!item).join(' - ');
      }

      return '??';
    },
    setUpAttachedServicePrices (idsToActivate) {
      let servicesList = {};

      for (let i = 0; i < this.services.length; i++) {
        let serviceID = this.services[i].id;
        servicesList[serviceID] = idsToActivate && idsToActivate.indexOf(serviceID) > -1;
      }

      return servicesList;
    },
    prepareAttachedServicePrices (objectToTransform) {
      let ids = Object.keys(objectToTransform);
      let output = [];

      for (let i = 0; i < ids.length; i++) {
        let serviceID = ids[i];

        if (objectToTransform[serviceID]) {
          output.push(serviceID);
        }
      }

      return output;
    },
    selectAllAttachedServices () {
      let keys = Object.keys(this.attached_services);

      for (let i = 0; i < keys.length; i++) {
        this.attached_services[keys[i]] = true;
      }
    },
    unselectAllAttachedServices () {
      let keys = Object.keys(this.attached_services);

      for (let i = 0; i < keys.length; i++) {
        this.attached_services[keys[i]] = false;
      }
    },
    increaseBoxes () {
      this.shipper_cartons_number++;
    },
    decreaseBoxes () {
      if (this.shipper_cartons_number > 1) {
        this.shipper_cartons_number--;
      }
    },
    openCustomerDiscountsPopup () {
      this.showCustomerDiscountsPopup = true;
    },
    closeCustomerDiscountsPopup () {
      this.showCustomerDiscountsPopup = false;
    },
    loadCustomerAfterDiscountsChange () {
      let customerID = this.$refs['order-customer'].customer_id;
      this.$refs['order-customer'].loadCustomer(customerID, false, false, true);
    },
    updateCartonsAmount (newValue) {
      this.preferredCartonsAmount = newValue.amount;
      this.notAllProductsHaveCartonsAmount = newValue.productsWithoutPieces;
    },
    removeConnectedOrder (index) {
      this.connected_orders.splice(index, 1);
    },
    addConnectedOrder (orderData) {
      this.connected_orders.push({
        id: orderData.id,
        status: orderData.status,
        order_name: orderData.order_name
      });
    },
    getStatusName (status) {
      return ListUtils.getKeyLabel(orderStatuses[this.$i18n.locale], status);
    },
    getStatusClass (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).replace(' outline', '');
    },
    getOutline (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).indexOf(' outline') > -1;
    },
    prepareDiscount (value) {
      if (Number.isNaN(value) || (typeof value === 'string' && value.trim() === '')) {
        return 0;
      }

      if (typeof value === 'string') {
        value = parseFloat(value.replace(',', '.'));
      }

      return value;
    },
    resetOrderDiscounts () {
      Vue.swal({
        title: this.$t('customer.discount.resetOrderDiscountsTitle'),
        text: this.$t('customer.discount.resetOrderDiscountsText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('customer.discount.resetDiscounts'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.setDiscountsToCustomerLevel();
        }
      });
    },
    setDiscountsToCustomerLevel () {
      Vue.set(this, 'discount_products', this.$refs['order-customer'].selectedCustomer.discount_products);
      Vue.set(this, 'discount_services', this.$refs['order-customer'].selectedCustomer.discount_services);
      Vue.set(this, 'discount_shipping', this.$refs['order-customer'].selectedCustomer.discount_shipping);
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    },
    updateShipperAdditionalPrice () {
      let shippingDiscount = this.prepareDiscount(this.discount_shipping);

      if (!this.include_discounts) {
        shippingDiscount = 0;
      }

      if (this.shipper_additional_price_before_discount) {
        this.shipper_additional_price = CustomDecimals.roundProperly(this.shipper_additional_price_before_discount - ((shippingDiscount / 100) * this.shipper_additional_price_before_discount));
      } else {
        this.shipper_additional_price = '0.00';
      }
    },
    setLastActiveTab () {
      let activeTab = window.localStorage.getItem('headwear-crm-last-active-tab');
      window.localStorage.removeItem('headwear-crm-last-active-tab');

      if (activeTab === 'order-data') {
        this.$refs['order-tabs'].setTab(0);
      } else if (activeTab === 'order-content') {
        this.$refs['order-tabs'].setTab(1);
      }
    },
    debouncedOrderNameCheck: debounce(function () {
      this.orderNameCheck();
    }, 500),
    orderNameCheck () {
      if (this.isEditMode && !this.skippedFirstOrderNameCheck && this.order_name === this.orderData.order_name) {
        this.skippedFirstOrderNameCheck = true;
        return;
      }

      if (this.order_name === '') {
        this.orderNameInUse = false;
        this.orderNameCheckInProgress = false;
        return;
      }

      if (this.lastCheckedOrderName === this.order_name) {
        return;
      }

      this.orderNameCheckInProgress = true;
      let orderID = parseInt(this.$route.params.id, 10);
      let paramsToSent = {
        order_name: this.order_name
      };

      if (orderID) {
        paramsToSent.order_id = orderID;
      }

      this.lastCheckedOrderName = this.order_name;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/check-order-name',
        formData: paramsToSent,
        skipErrorDisplay: true,
        successAction: (response) => {
          this.orderNameInUse = response.data.length;
          this.foundedOrderNames = response.data;
          this.orderNameCheckInProgress = false;
        },
        failAction: () => {
          this.orderNameCheckInProgress = false;
          this.orderNameInUse = false;
          this.foundedOrderNames = [];
        }
      });
    },
    showOrderNamePopup () {
      this.displayOrderNamePopup = true;
    },
    closeOrderNamePopup () {
      this.displayOrderNamePopup = false;
    },
    blockOrder () {
      console.log('ORDER BLOCK');
      if (this.orderData && this.orderData.id && !this.userIsCustomer && !this.isLocked) {
        this.$http.post('/orders/start-edit/' + parseInt(this.orderData.id, 10)).then(() => {}).catch(error => {
          console.log(error);
        });
      }
    },
    unblockOrder () {
      if (this.orderData && this.orderData.id && !this.userIsCustomer && !this.isLocked) {
        this.$bus.$emit('order-unblock-in-progress', true);

        this.$http.post('/orders/end-edit/' + parseInt(this.orderData.id, 10)).then(() => {
          this.$bus.$emit('order-unblock-in-progress', false);
        }).catch(error => {
          this.$bus.$emit('order-unblock-in-progress', false);
          console.log(error);
        });
      }
    },
    updateInvoiceDataRequest () {
      Vue.swal({
        title: this.$t('order.reallyWantToUpdateCustomerInvoiceData'),
        text: this.$t('order.reallyWantToUpdateCustomerInvoiceDataDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('order.yesUpdate'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.updateInvoiceData();
        }
      });
    },
    updateInvoiceData () {
      this.updateInvoiceDataInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/add-invoice-data/' + this.orderData.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          this.updateInvoiceDataInProgress = false;

          Vue.swal({
            title: this.$t('order.invoiceDataUpdated'),
            html: this.$t('order.invoiceDataUpdatedDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          }).then((result) => {
            if (result.value) {
              window.location.reload();
            }
          });
        },
        failAction: () => {
          this.updateInvoiceDataInProgress = false;
        }
      });
    },
    checkIfCanBeVirtualOrder () {
      setTimeout(() => {
        if (this.is_virtual && this.orderData.is_synchronised) {
          Vue.swal({
            title: this.$t('order.cannotBeVirtual'),
            html: this.$t('order.cannotBeVirtualDescSync'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.is_virtual = false;
          return;
        }

        if (this.is_virtual && !this.$refs['order-creator'].canBeVirtualOrder()) {
          Vue.swal({
            title: this.$t('order.cannotBeVirtual'),
            html: this.$t('order.cannotBeVirtualDesc'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.is_virtual = false;
        }
      }, 100);
    },
    showPriceChangePopup (productPrices, productServicePrices, globalServicePrices) {
      this.displayPriceChangesPopup = true;
      this.changedProductPrices = productPrices;
      this.changedProductServicePrices = productServicePrices;
      this.changedGlobalServicePrices = globalServicePrices;
      this.orderPricesUpdated = true;
    },
    hidePriceChangePopup () {
      this.displayPriceChangesPopup = false;
      this.changedProductPrices = {};
      this.changedProductServicePrices = {};
      this.changedGlobalServicePrices = {};
    },
    compareInvoiceData () {
      let result = true;
      let dataCompareResults = [];
      let fieldsToCompare = [
        'invoice_name',
        'invoice_street',
        'invoice_building',
        'invoice_locale',
        'invoice_postcode',
        'invoice_city',
        'invoice_tax_number',
        'invoice_country_prefix'
      ];

      for (let i = 0; i < fieldsToCompare.length; i++) {
        let fieldName = fieldsToCompare[i];
        if (this.orderData.customer[fieldName] !== this.orderData[fieldName]) {
          dataCompareResults.push({
            field: fieldName,
            customerValue: this.orderData.customer[fieldName],
            orderValue: this.orderData[fieldName],
            diff: true
          });
          result = false;
        } else {
          dataCompareResults.push({
            field: fieldName,
            customerValue: this.orderData.customer[fieldName],
            orderValue: this.orderData[fieldName],
            diff: false
          });
        }
      }

      if (result) {
        return true;
      }

      return dataCompareResults;
    },
    hideInvoiceDiffPopup () {
      this.displayInvoiceDiffPopup = false;
    },
    forceInvoiceRequestWithoutInvoiceDataCheck () {
      this.requestSettlementInvoice(true);
    },
    setBlockTransationTypeFieldStatus (newState) {
      if (this.blockDomesticTransactionType) {
        return;
      }

      if (this.custom_transaction_type) {
        return;
      }

      let oldState = this.blockExportTransactionType;
      Vue.set(this, 'blockExportTransactionType', newState);

      if (newState === true) {
        let previousTransacitonType = this.transaction_type;
        this.transaction_type = 'EXPORT';

        if (previousTransacitonType !== this.transaction_type) {
          Vue.swal({
            title: this.$t('order.transactionTypeSetToExport'),
            html: this.$t('order.transactionTypeSetToExportDesc'),
            type: 'info',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }

      if (oldState === true && newState === false) {
        Vue.swal({
          title: this.$t('order.transactionTypeFieldUnlocked'),
          html: this.$t('order.transactionTypeFieldUnlockedDesc'),
          type: 'info',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }
    },
    verifyTransactionType () {
      if (this.orderData.custom_transaction_type) {
        return;
      }

      this.blockExportTransactionType = this.orderData.block_export_transaction_type;
      this.blockDomesticTransactionType = this.orderData.block_domestic_transaction_type;
      this.blockNoEuVatTransactionType = this.orderData.block_no_eu_vat_transaction_type;

      if (this.orderData.block_export_transaction_type && this.orderData.transaction_type !== 'EXPORT') {
        Vue.set(this.orderData, 'transaction_type', 'EXPORT');

        Vue.swal({
          title: this.$t('order.transactionTypeSetToExport'),
          html: this.$t('order.transactionTypeSetToExportDescSaveNeeded'),
          type: 'info',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }

      if (this.orderData.block_domestic_transaction_type && this.orderData.transaction_type !== 'DOMESTIC') {
        Vue.set(this.orderData, 'transaction_type', 'DOMESTIC');

        Vue.swal({
          title: this.$t('order.transactionTypeSetToDomestic'),
          html: this.$t('order.transactionTypeSetToDomesticDescSaveNeeded'),
          type: 'info',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }

      if (this.orderData.block_no_eu_vat_transaction_type && this.orderData.transaction_type !== 'NO_EU_VAT') {
        Vue.set(this.orderData, 'transaction_type', 'NO_EU_VAT');

        Vue.swal({
          title: this.$t('order.transactionTypeSetToNoEuVAT'),
          html: this.$t('order.transactionTypeSetToNoEuVATDescSaveNeeded'),
          type: 'info',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }
    },
    openComplaintAddPopup () {
      this.showComplaintNewPopup = true;
    },
    closeComplaintAddPopup () {
      this.showComplaintNewPopup = false;
    },
    orderShipmentAddressChanged () {
      if (!this.isEditMode || this.orderSetUpInProgress) {
        return;
      }

      this.addressHasBeenChanged = true;
    },
    removeDate (dateField) {
      if (dateField === 'deadline') {
        Vue.set(this, 'deadline', null);
      }

      if (dateField === 'shipper_date') {
        Vue.set(this, 'shipper_date', null);
      }

      if (dateField === 'stash_to') {
        Vue.set(this, 'stash_to', null);
      }

      if (dateField === 'customer_order_date') {
        Vue.set(this, 'customer_order_date', null);
      }

      if (dateField === 'original_shipment_date') {
        Vue.set(this, 'original_shipment_date', null);
      }
    },
    newShipmentAdded () {
      if (!this.trackingNumberIsLocked) {
        this.trackingNumberIsLocked = true;

        Vue.swal({
          title: '',
          html: this.$t('order.createInvoiceOrChangeToSent'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }
    },
    saveOrderBeforeLeave (nextCallback) {
      // emulate save click to avoid second ask before leaving
      this.$bus.$emit('order-form-save-button-clicked');
      this.validateForm(nextCallback);
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    setUpTags (orderTags) {
      for (let i = 0; i < orderTags.length; i++) {
        let id = orderTags[i].id;
        this.tags[id] = true;
      }
    },
    prepareTags () {
      let output = [];
      let tagIDs = Object.keys(this.tags);

      for (let i = 0; i < tagIDs.length; i++) {
        if (this.tags[tagIDs[i]]) {
          output.push(tagIDs[i]);
        }
      }

      return output;
    },
    continousDecreaseBoxesStart () {
      this.decreaseBoxesTimerInterval = setInterval(() => {
        this.decreaseBoxes();
      }, 200);
    },
    continousDecreaseBoxesStop () {
      clearInterval(this.decreaseBoxesTimerInterval);
    },
    continousIncreaseBoxesStart () {
      this.increaseBoxesTimerInterval = setInterval(() => {
        this.increaseBoxes();
      }, 200);
    },
    continousIncreaseBoxesStop () {
      clearInterval(this.increaseBoxesTimerInterval);
    },
    usePreferredCartonsAmount () {
      if (!Number.isNaN(parseInt(this.preferredCartonsAmount, 10)) && parseInt(this.preferredCartonsAmount, 10) > 0) {
        this.shipper_cartons_number = parseInt(this.preferredCartonsAmount, 10);
      }
    },
    notifyClient () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/communications/send/' + parseInt(this.$route.params.id, 10),
        formData: {
          type: 'NEW_OFFER'
        },
        successTitle: this.$t('inquiries.sendForCustomerApprovalSuccessTitle'),
        successText: this.$t('inquiries.sendForCustomerApprovalSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('inquiries.sendForCustomerApprovalErrorText'),
        successAction: () => {
          this.$bus.$emit('contacts-refresh-list');
        }
      });
    },
    sendFollowUp () {
      if (this.followupInProgress) {
        return;
      }

      this.followupInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/send-offer-reminder/' + this.orderData.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          if (response.success) {
            this.followupInProgress = false;
            this.followUpSent = true;

            Vue.swal({
              title: this.$t('inquiries.followUpSent'),
              html: this.$t('inquiries.followUpSentText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.followupInProgress = false;
          }
        },
        failAction: () => {
          this.followupInProgress = false;
        }
      });
    },
    sendToHafciarnia () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/embriodery/send/' + parseInt(this.$route.params.id, 10),
        successTitle: this.$t('order.hafciarnia.sendToHafciarniaSuccessTitle'),
        successText: this.$t('order.hafciarnia.sendToHafciarniaSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.hafciarnia.sendToHafciarniaErrorText'),
        successAction: () => {
          this.$bus.$emit('order-wrapper-reload');
        }
      });
    },
    openAddIndentProductPopup (indentProductIndex) {
      this.displayAddIndentProductPopup = true;
      this.selectedIndentProductIndexToAdd = indentProductIndex;
    },
    closeAddIndentProductPopup () {
      this.displayAddIndentProductPopup = false;
      this.selectedIndentProductIndexToAdd = false;
    },
    openNewNotificationPopup () {
      this.displayNewNotificationPopup = true;
    },
    closeNewNotificationPopup () {
      this.displayNewNotificationPopup = false;
    },
    displayEmbroideryAlert () {
      this.showEmbroideryAlert = true;
    },
    displayBoxesAlert () {
      this.showBoxesAlert = true;
    },
    debouncedShippingPriceCheck: debounce(function () {
      this.shippingPriceCheck();
    }, 1000),
    shippingPriceCheck () {
      let shippingDiscount = this.prepareDiscount(this.discount_shipping);

      if (!this.include_discounts) {
        shippingDiscount = 0;
      }

      if (this.custom_shipping_price) {
        this.shipper_price = CustomDecimals.roundProperly((this.shipper_price_before_discount) - ((shippingDiscount / 100) * (this.shipper_price_before_discount)));
        this.shipper_additional_price = CustomDecimals.roundProperly((this.shipper_additional_price_before_discount) - ((shippingDiscount / 100) * (this.shipper_additional_price_before_discount)));
        return;
      }

      if (this.orderSetUpInProgress || this.free_freight) {
        return;
      }

      if (this.$refs['order-creator'] && this.$refs['order-creator'].totalProductItemsCount === 0) {
        this.shipper_price_before_discount = '0.00';
        this.shipper_price = '0.00';
        return;
      }

      let selectedShipper = this.$refs['order-shipper'].selectedShipper;
      let selectedShipperID = false;
      let boxesCount = this.shipper_cartons_number > 0 ? this.shipper_cartons_number : 1;
      let selectedAddress = this.$refs['order-address'].selectedAddress;
      let selectedCountryID = false;


      if (selectedShipper) {
        selectedShipperID = selectedShipper.id;
      }

      if (selectedAddress && selectedAddress.country_id) {
        selectedCountryID = selectedAddress.country_id;
      }

      if (this.isEditMode && !this.addressHasBeenChanged && this.orderData.address_country_id) {
        selectedCountryID = this.orderData.address_country_id;
      }

      let selectedCurrencyID = this.$refs['order-currency'].currency_id;

      if (!(selectedShipperID && selectedCountryID && selectedCurrencyID)) {
        return;
      }

      this.shippingPriceUpdateInProgress = true;
      this.errorDuringLoadingShippingPrice = false;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/get-shipper-price',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.errors.shipping_price_not_fetched'),
        formData: {
          shipper_id: selectedShipperID,
          conditions: {
            currency_id: selectedCurrencyID,
            country_id: selectedCountryID,
            boxes_count: boxesCount,
            products_count: this.$refs['order-creator'].totalProductItemsCount,
            discount_percent: shippingDiscount
          }
        },
        successAction: (response) => {
          this.shippingPriceUpdateInProgress = false;

          if (response.shipper_price && typeof response.shipper_price.price_before_discount !== 'undefined') {
            this.shipper_price_before_discount = CustomDecimals.roundProperly(response.shipper_price.price_before_discount);
            this.shipper_price = CustomDecimals.roundProperly(response.shipper_price.price);
            this.shipper_additional_price = CustomDecimals.roundProperly((this.shipper_additional_price_before_discount) - ((shippingDiscount / 100) * (this.shipper_additional_price_before_discount)));
          } else {
            this.errorDuringLoadingShippingPrice = true;
            this.shipper_price_before_discount = '0.00';
            this.shipper_price = '0.00';
          }
        },
        failAction: () => {
          this.errorDuringLoadingShippingPrice = true;
          this.shippingPriceUpdateInProgress = false;
        }
      });
    },
    updateDiscountedShippingPrice () {
      let shippingDiscount = this.prepareDiscount(this.discount_shipping);

      if (!this.include_discounts) {
        shippingDiscount = 0;
      }

      this.shipper_price = CustomDecimals.roundProperly((this.shipper_price_before_discount) - ((shippingDiscount / 100) * (this.shipper_price_before_discount)));
    },
    sendShipmentData () {
      let shipperID = null;
      let shippers = [];

      if (this.$refs['order-shipper']) {
        if (this.$refs['order-shipper'].selectedShipper) {
          shipperID = this.$refs['order-shipper'].selectedShipper.code;
        }

        if (this.$refs['order-shipper'].shippers) {
          shippers = JSON.parse(JSON.stringify(this.$refs['order-shipper'].shippers));
        }
      }

      this.$bus.$emit('order-receive-shipment-data', {
        'trackingNumber': this.tracking_number,
        'shipperID': shipperID,
        'shippers': shippers
      });
    },
    disableCustomerDiscounts () {
      Vue.set(this, 'include_discounts', false);
    },
    enableCustomerDiscounts () {
      Vue.set(this, 'include_discounts', true);
    },
    closeErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', false);
    },
    openErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', true);
    },
    toggleAutoSpecialRequirement (valueToSet) {
      if (this.isEditMode) {
        return;
      }

      this.is_special_requirement = valueToSet;
    },
    setSpecialRequirement (valueToSet) {
      if (this.isEditMode) {
        return;
      }

      this.special_requirement = valueToSet;

      if (valueToSet) {
        Vue.swal({
          title: this.$t('order.hasSpecialRequirementsWarningPopupTitle'),
          text: this.$t('order.hasSpecialRequirementsWarningPopupDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        }).then(() => {});
      }
    },
    closeConfirmationsPopup () {
      this.showConfirmationsPopup = false;
    },
    handleConfirmations (confirmations) {
      Vue.set(this, 'confirmations', confirmations);
      this.showConfirmationsPopup = false;
      this.submitConfirmedForm(this.nextCallbackForConfirmations);
    },
    excludeFromERP () {
      if (this.excludeFromErpInProgress) {
        return;
      }

      this.excludeFromErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/exclude-from-erp-integration/' + this.orderData.id,
        formData: {
          exclude_from_erp_integration: 1
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.excludeFromErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.excludeFailedTitle'),
              html: this.$t('order.erp.excludeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.exclude_from_erp_integration = true;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.excludeFromErpInProgress = false;
        }
      });
    },
    includeToERP () {
      if (this.includeToErpInProgress) {
        return;
      }

      this.includeToErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/exclude-from-erp-integration/' + this.orderData.id,
        formData: {
          exclude_from_erp_integration: 0
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.includeToErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.includeFailedTitle'),
              html: this.$t('order.erp.includeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.exclude_from_erp_integration = false;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.includeToErpInProgress = false;
        }
      });
    },
    sendAgainToEmbroidery () {
      this.sendingAgainToEmbroideryInProgress = true;

      FormUtils.submitInBackground({
        method: 'post',
        $http: this.$http,
        endpoint: '/orders/embriodery/send-again/' + this.orderData.id,
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.hafciarnia.sendToHafciarniaErrorText'),
        successAction: (response) => {
          if (response.success) {
            this.sendingAgainToEmbroideryInProgress = false;
            this.hasBeenSendAgainToEmbroidery = true;

            Vue.swal({
              title: this.$t('order.hafciarnia.sendingAgainToEmbroideryTitle'),
              html: this.$t('order.hafciarnia.sendingAgainToEmbroideryDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('order.hafciarnia.sendToHafciarniaErrorText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.sendingAgainToEmbroideryInProgress = false;
        }
      });
    },
    openGusComparisionPopup (data) {
      this.showGusComparisionPopup = true;
      this.receivedGusData = data;
    },
    closeGusComparisionPopup () {
      this.showGusComparisionPopup = false;
      this.receivedGusData = null;
    },
    lockOrderEdit () {
      if (this.lockInProgress) {
        return;
      }

      this.lockInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/change-force-can-update/' + this.orderData.id,
        formData: {
          force_can_update: 0
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.lockInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.lockSuccessTitle'),
              html: this.$t('order.lockSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            }).then(() => {
              window.location.reload();
            });
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.lockInProgress = false;
        }
      });
    },
    orderCustomerHasBeenReset () {
      this.addressHasBeenChanged = true;
      this.updateShipmentPrice();
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-update-shipment-cost', this.updateShipmentCost);
    this.$bus.$off('order-data-fetch-error', this.dataFetchError);
    this.$bus.$off('order-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$off('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$off('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$off('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$off('order-show-add-address-popup', this.showAddressNewPopup);
    this.$bus.$off('customer-view-address-popup-close', this.closeAddressViewPopup);
    this.$bus.$off('customer-view-contact-person-popup-close', this.closeContactPersonViewPopup);
    this.$bus.$off('order-show-address-edit-popup', this.openAddressEditPopup);
    this.$bus.$off('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$off('order-show-address-view-popup', this.openAddressViewPopup);
    this.$bus.$off('order-show-contact-person-view-popup', this.openContactPersonViewPopup);
    this.$bus.$off('container-search-popup-close', this.closeContainerSearchPopup);
    this.$bus.$off('container-search-popup-open', this.openContainerSearchPopup);
    this.$bus.$off('purchase-search-popup-close', this.closePurchaseSearchPopup);
    this.$bus.$off('purchase-search-popup-open', this.openPurchaseSearchPopup);
    this.$bus.$off('purchase-add-popup-close', this.closePurchaseAddPopup);
    this.$bus.$off('purchase-add-popup-open', this.openPurchaseAddPopup);
    this.$bus.$off('purchase-edit-popup-close', this.closePurchaseEditPopup);
    this.$bus.$off('purchase-edit-popup-open', this.openPurchaseEditPopup);
    this.$bus.$off('show-customer-discounts-popup', this.openCustomerDiscountsPopup);
    this.$bus.$off('close-customer-discounts-popup', this.closeCustomerDiscountsPopup);
    this.$bus.$off('order-select-customer-reload-customers-list', this.loadCustomerAfterDiscountsChange);
    this.$bus.$off('order-updated-products-qty', this.updateCartonsAmount);
    this.$bus.$off('order-updated-products-ids', this.updateCartonsAmount);
    this.$bus.$off('order-customer-id-changed', this.updateCustomerRelatedItems);
    this.$bus.$off('connected-orders-add', this.addConnectedOrder);
    this.$bus.$off('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$off('close-order-contact-edit-popup', this.closeOrderContactEditPopup);
    this.$bus.$off('close-order-name-popup', this.closeOrderNamePopup);
    this.$bus.$off('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$off('show-order-contact-edit-popup', this.openOrderContactEditPopup);
    this.$bus.$off('show-price-changes-popup', this.showPriceChangePopup);
    this.$bus.$off('close-price-changes-popup', this.hidePriceChangePopup);
    this.$bus.$off('close-invoice-diff-popup', this.hideInvoiceDiffPopup);
    this.$bus.$off('force-invoice-settlement-request', this.forceInvoiceRequestWithoutInvoiceDataCheck);
    this.$bus.$off('order-block-transaction-type-field', this.setBlockTransationTypeFieldStatus);
    this.$bus.$off('show-complaint-add-popup', this.openComplaintAddPopup);
    this.$bus.$off('close-complaint-add-popup', this.closeComplaintAddPopup);
    this.$bus.$off('order-update-shipment-address-id', this.orderShipmentAddressChanged);
    this.$bus.$off('order-new-shipment-added', this.newShipmentAdded);
    this.$bus.$off('order-form-save-before-leave', this.saveOrderBeforeLeave);
    this.$bus.$off('show-add-indent-product-popup', this.openAddIndentProductPopup);
    this.$bus.$off('close-add-indent-product-popup', this.closeAddIndentProductPopup);
    this.$bus.$off('order-change-indent-product-from-popup', this.reloadProductsAfterIndentProductAdd);
    this.$bus.$off('show-order-notification-new-popup', this.openNewNotificationPopup);
    this.$bus.$off('close-order-notification-new-popup', this.closeNewNotificationPopup);
    this.$bus.$off('show-order-embroidery-alert', this.displayEmbroideryAlert);
    this.$bus.$off('show-order-boxes-alert', this.displayBoxesAlert);
    this.$bus.$off('order-check-shipping-price', this.debouncedShippingPriceCheck);
    this.$bus.$off('order-send-request-shipment-data', this.sendShipmentData);
    this.$bus.$off('order-disable-customer-discounts', this.disableCustomerDiscounts);
    this.$bus.$off('order-enable-customer-discounts', this.enableCustomerDiscounts);
    this.$bus.$off('show-order-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$off('close-order-erp-integration-popup', this.closeErpIntegrationPopup);
    this.$bus.$off('ui-select-customer-auto-special-requirement', this.toggleAutoSpecialRequirement);
    this.$bus.$off('ui-select-customer-set-special-requirement', this.setSpecialRequirement);
    this.$bus.$off('close-order-confirmations-popup', this.closeConfirmationsPopup);
    this.$bus.$off('order-set-order-confirmations', this.handleConfirmations);
    this.$bus.$off('show-gus-comparision-popup', this.openGusComparisionPopup);
    this.$bus.$off('close-gus-comparision-popup', this.closeGusComparisionPopup);
    this.$bus.$off('order-check-customer-tax-number-before-invoice', this.checkCustomerTaxNumberBeforeInvoice);
    this.$bus.$off('order-customer-has-been-reset', this.orderCustomerHasBeenReset);
    // clear order interval
    clearInterval(this.blockInterval);
    this.unblockOrder();
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .5;
  pointer-events: none;
}

.text-bold {
  font-weight: bold;
}
.has-advance-issue {
  background: #f44;
}

.has-advance-issue-alert {
  border: 2px solid #fff;
  border-radius: 10px;
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  padding: 10px;
  position: relative;
  top: 10px;
  text-align: center;
}

.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

pre {
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  padding: 20px;
  white-space: pre-wrap;
}

.order-status-wrapper {
  width: 400px;

  & > div {
    display: flex;
    width: 100%;
  }

  /deep/ .v-select {
    width: 100%;

    .vs__dropdown-toggle {
      border: 1px solid #e1e5eb;
    }
  }
}

.attach-service-prices-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  list-style-type: none;
  margin: 20px 0 0 0;
  padding: 20px;

  li {
    font-size: 12px;
  }
}

.order-priority-checkbox,
.order-confirmation-checkbox {
  font-size: 15px;
  padding-top: 5px;
  width: 240px;
}

.order-priority-checkbox {
  width: 150px;
}

.is-disabled {
  opacity: .5;
}

.vdp-datepicker.text-danger /deep/ .form-control {
  color: var(--danger);
}

.order-tags-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  list-style-type: none;
  margin: 20px 0 0 0;
  padding: 20px 20px 10px 20px;

  li {
    font-size: 12px;
  }
}

.special-requirements-not-empty {
  border: 2px solid #c4183c;
}

.has-special-requirements {
  color: #fff!important;
  font-size: 150%;
  margin-bottom: 2rem;
  margin-left: -1.875rem;
  margin-right: -1.875rem;
  margin-top: -1.875rem;
  text-align: center;
  width: calc(100% + (2 * 1.875rem));

  i {
    font-size: 100%;
  }
}
</style>
